<template>
  <div class="sales-counter-page">
  <b-row>
    <b-col>

      <!-- HEADER -->
      <b-card class="mb-2 sc-mobile-header">
        <b-row align-v="center">
          <b-col>
            <template v-if="ctx.customer">
              <div class="sc-customer-title">
                <h4 class="card-title mb-0">{{ ctx.customer.name }}</h4>
                <b-button v-if="canChangeOnlineContext"
                          size="sm"
                          variant="outline-secondary"
                          class="sc-change-customer-btn"
                          v-b-tooltip.hover
                          title="Cambiar cliente"
                          @click="openCustomerSelector">
                  <AppIcon name="person-lines-fill" />
                </b-button>
              </div>
              <div v-if="ctx.points_sales_id && ctx.price_list_id && ctx.defaultMethodsPaymentId" class="sc-customer-config">
                <span>
                  <AppIcon name="tags" class="mr-1" />
                  <strong class="sc-customer-config-label">Lista:</strong>
                  <span class="sc-customer-config-value">{{ priceListLabel }}</span>
                </span>
                <span>
                  <AppIcon name="credit-card" class="mr-1" />
                  <strong class="sc-customer-config-label">Método:</strong>
                  <span class="sc-customer-config-value">{{ defaultMethodsPaymentLabel }}</span>
                </span>
                <span class="sc-point-sale-line">
                  <AppIcon name="building" class="mr-1" />
                  <strong class="sc-customer-config-label">Punto de venta:</strong>
                  <b-form-select v-if="canChangeOnlineContext"
                                 v-model.number="contextSelection.pointSaleId"
                                 :options="pointSaleOptions"
                                 size="sm"
                                 class="sc-point-sale-select"
                                 @change="onPointSaleSelected" />
                  <span v-else class="sc-customer-config-value">{{ pointSaleLabel }}</span>
                  <span v-if="isDeclaredPointSale" class="sc-electronic-label">
                    <AppIcon name="cloud" class="mr-1" />Electrónico
                  </span>
                </span>
              </div>
            </template>
            <h4 class="card-title mb-0 text-muted" v-else>Venta Mostrador</h4>
          </b-col>
          <b-col cols="auto" class="d-flex align-items-center" style="gap:6px;flex-wrap:wrap">
            <b-badge :variant="connectionStatusVariant"
                     class="p-2 sc-connection-badge"
                     :class="'sc-connection-badge-' + connectionStatus"
                     v-b-tooltip.hover
                     :title="connectionStatusTitle">
              <AppIcon :name="connectionStatusIcon" class="mr-1" />
              {{ connectionStatusLabel }}
            </b-badge>

            <!-- Catálogo offline: texto informativo (no botón) -->
            <span class="sc-header-catalog"
                  v-b-tooltip.hover
                  :title="'Catálogo de productos guardado localmente para uso sin conexión.' + (catalogUpdatedLabel ? ' Última actualización: ' + catalogUpdatedLabel + '.' : '')"
                  :class="{ 'text-success': offlineCatalog.status === 'ready',
                            'text-info':    offlineCatalog.status === 'updating',
                            'text-danger':  offlineCatalog.status === 'error',
                            'text-warning': offlineCatalog.status === 'empty',
                            'text-muted':   !offlineCatalog.status }">
              <b-spinner v-if="offlineCatalog.status === 'updating'" small class="mr-1"></b-spinner>
              <AppIcon v-else :name="catalogStatusIcon" class="mr-1" />
              Catálogo: {{ catalogStatusLabel }}
              <template v-if="offlineCatalog.productsCount"> · {{ offlineCatalog.productsCount }}p</template>
              <template v-if="catalogUpdatedLabel"> · {{ catalogUpdatedLabel }}</template>
              <template v-if="offlineCatalog.status === 'updating' && offlineCatalog.progress.total">
                ({{ offlineCatalog.progress.current }}/{{ offlineCatalog.progress.total }} · {{ offlineCatalog.progress.percent }}%)
              </template>
              <span v-if="offlineCatalog.error" class="ml-1 small">— {{ offlineCatalog.error }}</span>
            </span>

            <!-- Botón actualizar catálogo -->
            <span v-if="!ctxErrors.length && connectionStatus === 'offline'"
                  v-b-tooltip.hover title="Sin conexión — no se puede actualizar el catálogo">
              <b-button variant="outline-secondary" size="sm" disabled>
                <AppIcon name="wifi-off" />
              </b-button>
            </span>
            <span v-else-if="!ctxErrors.length && offlineCatalog.status === 'updating'">
              <b-button variant="outline-info" size="sm" disabled>
                <b-spinner small></b-spinner>
              </b-button>
            </span>
            <b-button v-else-if="!ctxErrors.length"
                      variant="outline-secondary" size="sm"
                      v-b-tooltip.hover title="Conectado a la API — clic para actualizar catálogo"
                      @click="refreshCache">
              <AppIcon name="refresh" />
            </b-button>

            <div class="sc-header-sep"></div>

            <!-- Cola de sincronización y errores -->
            <b-badge v-if="queueStats.pending > 0 || queueStats.processing > 0"
                     variant="warning" class="p-2 sales-counter-queue-retry"
                     @click="showQueueErrors()">
              <b-icon icon="clock" class="mr-1"></b-icon>
              {{ queueStats.pending + queueStats.processing }} pendiente(s)
            </b-badge>
            <b-badge v-if="queueStats.failed > 0"
                     variant="danger" class="p-2 sales-counter-queue-retry"
                     @click="showQueueErrors()">
              <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
              {{ queueStats.failed }} error(es) — Ver detalle
            </b-badge>
            <b-badge v-if="queueStats.staleProcessing > 0"
                     variant="dark" class="p-2 sales-counter-queue-retry"
                     @click="recoverQueueStuck()">
              <b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>
              {{ queueStats.staleProcessing }} trabado(s)
            </b-badge>
            <b-badge v-if="queueStats.lastSyncAt"
                     variant="light" class="p-2 sc-header-sync-badge"
                     v-b-tooltip.hover
                     :title="'Último comprobante sincronizado con el servidor: ' + queueLastSyncFullLabel">
              <div class="sc-header-sync-line"><b-icon icon="check2-circle" class="mr-1"></b-icon>Sync</div>
              <div class="sc-header-sync-when">{{ queueLastSyncLabel }}</div>
            </b-badge>
            <b-badge v-if="offlineStorage.ready === false"
                     variant="danger" class="p-2 sales-counter-queue-retry"
                     @click="checkOfflineStorage()">
              <b-icon icon="hdd" class="mr-1"></b-icon> Storage offline
            </b-badge>

            <div class="sc-header-sep"></div>

            <b-button variant="link"
                      class="text-white p-0 sc-focus-toggle"
                      @click="toggleFocusMode()"
                      :title="focusMode ? 'Salir de pantalla completa' : 'Pantalla completa'">
              <AppIcon name="arrows-fullscreen" />
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- ── APERTURA / CIERRE DE CAJA ── -->
      <CashSession ref="cashSession"
                   :visible.sync="cajaFlow.show"
                   :assigned-cash-id="assignedCashId"
                   :is-cash-blocked="queueHasBlockingItemsForCaja"
                   :open-items-for-cash-ids="queueOpenItemsForCashIds"
                   :queue-state="queueStats"
                   :point-sale-nd="cashPointSaleNd"
                   @cajas-loaded="setCajasStatus"
                   @opened="onCajaOpened"
                   @notify="showToast"
                   @show-queue="showQueueErrors" />

      <!-- ── PANTALLA DE PAGO ── -->
      <div v-if="!cajaFlow.show && paymentShow" class="sc-payment-screen" :style="{ height: scanScreenMinHeight + 'px' }">
      <b-card class="sc-payment-card">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <b-icon icon="cash" class="mr-2"></b-icon>
              Cobro
            </h5>
            <div class="sc-payment-total-header">{{ formatCurrency(total) }}</div>
          </div>
        </template>

        <!-- Caja asignada (si existe) -->
        <template v-if="preferredPaymentLine">
          <div class="sc-section-label mb-1">{{ preferredPaymentLine.preferredType === 'user' ? 'Tu caja' : 'Caja del punto de venta' }}</div>
          <div class="sc-methods-list mb-3">
            <div class="sc-method-row"
                 :class="preferredPaymentLine.selected ? 'sc-method-active' : ''"
                 @click="toggleLine(preferredPaymentLine._idx)">
              <div class="sc-pay-num mr-2"
                   :class="preferredPaymentLine.selected ? 'sc-pay-num-on' : ''">
                {{ preferredPaymentLine._idx + 1 }}
              </div>
              <div class="flex-grow-1">
                <strong>{{ preferredPaymentLine.caja.name }}</strong>
              </div>
              <div v-if="preferredPaymentLine.selected" class="sc-amount-block" @click.stop>
                <span class="sc-amount-label">Monto $</span>
                <b-form-input type="text"
                              inputmode="decimal"
                              :value="paymentLines[preferredPaymentLine._idx].amountDisplay"
                              class="sc-amount-input"
                              @input="onPaymentAmountInput(preferredPaymentLine._idx, $event)"
                              @focus="onPaymentAmountFocus"
                              @blur="formatPaymentAmountInput(preferredPaymentLine._idx)" />
              </div>
            </div>
          </div>
        </template>

        <!-- Otras cajas / todas las cajas si no hay asignada -->
        <template v-if="otherPaymentLines.length">
          <div class="sc-section-label mb-1">{{ preferredPaymentLine ? 'Otras cajas' : 'Caja' }}</div>
          <div class="sc-methods-list mb-2">
            <div v-for="line in otherPaymentLines" :key="line.caja.id"
                 class="sc-method-row"
                 :class="line.selected ? 'sc-method-active' : ''"
                 @click="toggleLine(line._idx)">
              <div class="sc-pay-num mr-2"
                   :class="line.selected ? 'sc-pay-num-on' : ''">
                {{ line._idx + 1 }}
              </div>
              <div class="flex-grow-1">
                <strong>{{ line.caja.name }}</strong>
              </div>
              <div v-if="line.selected" class="sc-amount-block" @click.stop>
                <span class="sc-amount-label">Monto $</span>
                <b-form-input type="text"
                              inputmode="decimal"
                              :value="paymentLines[line._idx].amountDisplay"
                              class="sc-amount-input"
                              @input="onPaymentAmountInput(line._idx, $event)"
                              @focus="onPaymentAmountFocus"
                              @blur="formatPaymentAmountInput(line._idx)" />
              </div>
            </div>
          </div>
        </template>

        <div v-if="!paymentLines.length" class="text-muted small p-2 mb-2">
          No hay cajas disponibles.
        </div>

        <!-- Hint de teclas + calculadora de vuelto en el mismo renglón -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="sc-shortcuts">
            <span class="sc-shortcut">
              <kbd>Ctrl</kbd>+<kbd>1</kbd>–<kbd>{{ Math.min(paymentLines.length, 9) }}</kbd> Seleccionar
            </span>
            <span class="sc-shortcut"><kbd>F12</kbd> Confirmar (con caja seleccionada)</span>
            <span class="sc-shortcut">
              <kbd>ESC</kbd> {{ paymentLines.some(l => l.selected) ? 'Deseleccionar' : 'Volver' }}
            </span>
          </div>
          <!-- Calculadora de vuelto: ayuda para dar el cambio, no modifica los importes del cobro -->
          <b-button variant="secondary" class="ml-3 flex-shrink-0"
                    style="font-size:1.05rem;font-weight:600;padding:8px 16px"
                    @click="openVuelto">
            <b-icon icon="calculator" class="mr-1"></b-icon>Calcular vuelto
            <span class="sc-kbd-hint sc-kbd-hint-light ml-1" style="font-size:13px">F8</span>
          </b-button>
        </div>


        <!-- Validación diferencia -->
        <div v-if="selectedPaymentTotal > 0 && Math.abs(paymentDiffCents) >= 1"
             class="sc-payment-diff-alert mt-3"
             :class="paymentDiff < 0 ? 'sc-payment-diff-danger' : 'sc-payment-diff-warning'">
          <b-icon :icon="paymentDiff < 0 ? 'exclamation-octagon-fill' : 'exclamation-triangle-fill'" class="sc-payment-diff-icon"></b-icon>
          <div>
            <div class="sc-payment-diff-title">{{ paymentDiffTitle }}</div>
            <div class="sc-payment-diff-amount">{{ formatCurrency(Math.abs(paymentDiff)) }}</div>
          </div>
        </div>

      </b-card>
        <div class="sc-bottom-bar">
          <div class="sc-action-bar">
            <b-button variant="outline-secondary" @click="paymentBack" class="sc-btn-cancel">
              <b-icon icon="arrow-left" class="mr-1"></b-icon>
              {{ paymentLines.some(l => l.selected) ? 'Deseleccionar' : 'Volver' }}
              <span class="sc-kbd-hint sc-kbd-hint-secondary ml-2">ESC</span>
            </b-button>
            <b-button variant="success" size="lg" @click="confirmPayment"
                      :disabled="!canConfirm || isSaving" ref="btnConfirm"
                      class="sc-btn-cobrar">
              <b-spinner v-if="isSaving" small class="mr-1"></b-spinner>
              <b-icon v-else icon="check-circle" class="mr-1"></b-icon>
              Confirmar
              <span class="sc-kbd-hint sc-kbd-hint-light ml-2">F12</span>
            </b-button>
          </div>
        </div>
      </div>

      <!-- ── ERRORES DE CONFIGURACIÓN ── -->
      <div v-else-if="!cajaFlow.show && ctxErrors.length" class="sc-config-errors">
        <div class="sc-config-errors-header">
          <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
          El mostrador no está listo para usar — revisá la configuración
        </div>
        <div v-for="(err, i) in ctxErrors" :key="i"
             class="sc-config-error-item"
             :class="'sc-config-error-' + err.variant">
          <b-icon :icon="err.icon" class="sc-config-error-icon"></b-icon>
          <div class="sc-config-error-body">
            <div class="sc-config-error-title">{{ err.title }}</div>
            <div class="sc-config-error-detail">{{ err.detail }}</div>
          </div>
        </div>
      </div>

      <!-- ── PANTALLA DE ESCANEO ── -->
      <div v-else-if="!cajaFlow.show" class="sc-scan-screen" ref="scanScreen" :style="{ height: scanScreenMinHeight + 'px' }">

        <!-- Barcode input + controles -->
        <b-card class="mb-2 sc-barcode-card">
          <b-row align-v="center">
            <b-col>
              <!-- Modo escáner (default) -->
              <div v-if="!productSearch.active" style="position:relative">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text class="sc-barcode-icon">
                      <b-icon v-if="!barcode.searching" icon="upc-scan"></b-icon>
                      <b-spinner v-else small></b-spinner>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input ref="barcodeInput"
                                v-model="barcode.buffer"
                                placeholder="Escanear código de barras..."
                                class="sc-barcode-input"
                                :disabled="!ctx.ready"
                                autocomplete="off"
                                readonly />
                </b-input-group>
                <div v-if="!ctx.ready" class="text-muted small mt-1">
                  <b-spinner small class="mr-1"></b-spinner> Cargando configuración...
                </div>
              </div>

              <!-- Modo búsqueda manual (F3) -->
              <div v-else style="position:relative">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text class="sc-barcode-icon" :class="{ 'sc-quick-concept-icon': isQuickConceptShortcutActive }">
                      <b-spinner v-if="productSearch.loading" small></b-spinner>
                      <b-icon v-else-if="isQuickConceptShortcutActive" icon="list-ul"></b-icon>
                      <b-icon v-else icon="search"></b-icon>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    ref="productSearchInput"
                    v-model="productSearch.query"
                    placeholder="Nombre, código de barra, categoría... y presioná Enter"
                    class="sc-barcode-input"
                    :class="{ 'sc-quick-concept-input': isQuickConceptShortcutActive }"
                    autocomplete="off"
                    @input="onProductSearchInput"
                    @keydown.down.prevent="productSearchMoveDown"
                    @keydown.up.prevent="productSearchMoveUp"
                    @keydown.enter.prevent="productSearchConfirm"
                    @blur="productSearchBlur"
                    @focus="productSearchFocus"
                  />
                  <b-input-group-append>
                    <b-button variant="outline-secondary" tabindex="-1" @mousedown.prevent="closeProductSearch"
                              title="Volver al escáner (F3)">
                      <b-icon icon="x"></b-icon>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
                <div class="small mt-1 sc-search-shortcuts">
                  <b-icon icon="search" class="mr-1"></b-icon>
                  Presioná <kbd>Enter</kbd> para buscar · <kbd>↑↓</kbd> navegar · <kbd>F3</kbd> volver al escáner · <kbd>**importe</kbd> agregar concepto
                </div>
                <div v-if="isQuickConceptShortcutActive" class="sc-quick-concept-hint">
                  <b-icon icon="list-ul" class="mr-1"></b-icon>
                  Modo concepto rápido <span v-if="quickConceptShortcutPrice">· {{ formatCurrency(quickConceptShortcutPrice) }}</span>
                  <span class="ml-1">— presioná Enter para continuar</span>
                </div>

                <!-- Resultados -->
                <div v-if="productSearch.show" class="sc-search-results">
                  <div v-if="productSearch.loading" class="sc-search-empty text-muted">
                    <b-spinner small class="mr-1"></b-spinner> Buscando...
                  </div>
                  <div v-else-if="!productSearch.results.length" class="sc-search-empty text-muted">
                    Sin resultados para "<strong>{{ productSearch.query }}</strong>"
                  </div>
                  <template v-else>
                    <div v-for="(item, idx) in productSearch.results"
                         :key="item.productId"
                         class="sc-search-item"
                         :class="{ 'sc-search-item-active': idx === productSearch.activeIdx }"
                         @mousedown.prevent="addFromSearch(item)">
                      <div class="sc-search-item-body">
                        <div class="sc-search-item-name">{{ item.product.name }}</div>
                        <div class="sc-search-item-meta">
                          <span class="sc-search-code">{{ item.code }}</span>
                          <span v-if="item.categoryName"> · {{ item.categoryName }}</span>
                          <span v-if="item.subcategoryName"> / {{ item.subcategoryName }}</span>
                        </div>
                      </div>
                      <div class="sc-search-item-price">{{ formatCurrency(item.price) }}</div>
                    </div>
                  </template>
                </div>
              </div>
            </b-col>

            <b-col cols="auto" class="d-flex align-self-start" style="gap:6px">
              <div :class="['sc-header-btn', productSearch.active ? 'sc-header-btn-primary-on' : 'sc-header-btn-primary']"
                   :style="!ctx.ready ? 'opacity:.45;pointer-events:none' : ''"
                   @click="productSearch.active ? closeProductSearch() : openProductSearch()"
                   v-b-tooltip.hover title="Buscar producto por nombre / código / categoría (F3)">
                <b-icon icon="search" class="sc-header-btn-icon"></b-icon>
                <span class="sc-header-btn-label">Buscar <kbd class="sc-header-kbd">F3</kbd></span>
              </div>
              <div v-if="hasAssignedCash && apiOnline"
                   class="sc-header-btn sc-header-btn-purple" @click="openCajaCierre"
                   v-b-tooltip.hover title="Ver estado, movimientos y opciones de la caja">
                <b-icon icon="cash-stack" class="sc-header-btn-icon"></b-icon>
                <span class="sc-header-btn-label">Estado caja</span>
              </div>
            </b-col>
          </b-row>

        </b-card>

        <!-- Tabla de productos / estado vacío -->
        <b-card class="sc-items-card" :class="{ 'sc-items-empty': !items.length }">

          <b-table v-if="items.length"
                   :items="reversedItems" :fields="tableFields"
                   hover bordered responsive
                   class="sc-table mb-0"
                   :tbody-tr-class="rowClass"
                   @row-clicked="row => selectedItem = row">

            <template v-slot:cell(idx)="{ index }">
              <span class="sc-row-num">{{ items.length - index }}</span>
            </template>

            <template v-slot:cell(name)="{ item }">
              <div class="sc-prod-name">{{ item.name }}</div>
              <div class="sc-prod-code">
                <template v-if="item.item_type === 'concept'">
                  <b-icon icon="list-ul" class="mr-1"></b-icon>Concepto
                </template>
                <template v-else>{{ item.code }}</template>
              </div>
            </template>

            <template v-slot:cell(quantity)="data">
              <b-form-input type="number" v-model.number="data.item.quantity"
                            min="1" step="1"
                            style="width:90px" class="text-center sc-qty-input"
                            @focus="$event.target.select()"
                            @change="onQtyChange(data.item)"
                            @click.stop />
            </template>

            <template v-slot:cell(price)="data">
              <span class="sc-price">{{ formatCurrency(data.item.price) }}</span>
            </template>

            <template v-slot:cell(subtotal)="data">
              <strong class="sc-subtotal">{{ formatCurrency(data.item.quantity * data.item.price) }}</strong>
            </template>

            <template v-slot:cell(actions)="data">
              <b-button size="sm" variant="outline-danger" @click.stop="confirmDeleteItem(items.length - 1 - data.index, data.item)"
                        title="Eliminar" class="sc-del-btn">
                <b-icon icon="trash"></b-icon>
              </b-button>
            </template>
          </b-table>

          <div v-else class="text-center py-5 sc-empty-state">
            <b-icon icon="upc-scan" class="sc-empty-icon mb-3"></b-icon>
            <div class="sc-empty-text" v-if="ctx.ready">Escaneá un producto para empezar</div>
          </div>
        </b-card>

        <!-- Barra de total + acciones -->
        <div class="sc-bottom-bar">
          <div class="sc-total-area" v-if="items.length">
            <div class="sc-total-left">
              <div class="sc-total-label">{{ itemsCount }} ítem(s)</div>
              <div class="sc-total-discount-control">
                <label for="sales-counter-general-discount">Descuento</label>
                <div class="sc-total-discount-input-group">
                  <b-form-input id="sales-counter-general-discount"
                                type="text"
                                inputmode="decimal"
                                :value="generalDiscount.percentageInput"
                                @input="onGeneralDiscountInput($event)"
                                @focus="onGeneralDiscountFocus"
                                @blur="formatGeneralDiscountInput" />
                  <span>%</span>
                </div>
              </div>
            </div>
            <div class="sc-total-summary">
              <div v-if="generalDiscountCents" class="sc-total-discount">
                Descuento: −{{ formatCurrency(generalDiscountAmount) }}
              </div>
              <div class="sc-total-amount">{{ formatCurrency(total) }}</div>
            </div>
          </div>

          <div class="sc-action-bar">
            <b-button variant="outline-dark" @click="resetItems"
                      :disabled="!items.length" class="sc-btn-cancel">
              <b-icon icon="x-circle" class="mr-1"></b-icon> Cancelar
            </b-button>

            <div class="d-flex" style="gap:8px">
              <b-button variant="outline-danger" @click="confirmDeleteByCodeOpen"
                        :disabled="!items.length" class="sc-btn-action"
                        title="Escanear el código del producto que el cliente devuelve">
                <b-icon icon="upc-scan" class="mr-1"></b-icon>
                Eliminar
                <span class="sc-kbd-hint">F10</span>
              </b-button>

<b-button variant="success" size="lg" @click="openPayment"
                        :disabled="!items.length || barcode.searching || !ctx.ready"
                        class="sc-btn-cobrar">
                <b-icon icon="cash" class="mr-1"></b-icon>
                Cobrar
                <span class="sc-kbd-hint sc-kbd-hint-light">F12</span>
              </b-button>
            </div>
          </div>
        </div>

      </div>

      <!-- ── MODAL: Cambiar cliente ── -->
      <b-modal v-model="modal.customerSelector.active"
               size="lg"
               no-close-on-backdrop
               hide-footer
               modal-class="sc-customer-modal">
        <template #modal-header>
          <div class="sc-customer-modal-header">
            <b-icon icon="person-lines-fill" class="mr-2"></b-icon>
            Cambiar cliente
            <span v-if="items.length" class="sc-customer-modal-header-warn ml-3">
              <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
              Cancelará el ticket ({{ items.length }} producto(s))
            </span>
          </div>
        </template>
        <p class="sc-customer-modal-hint">
          Buscá el cliente para que la factura salga con sus <strong>datos fiscales</strong>.
        </p>
        <FindObject v-if="modal.customerSelector.active"
                    render="search"
                    type="customers"
                    tag="Cliente"
                    @select-object="onCustomerSelected($event)"
                    :key="'sales-counter-customer-modal-' + ctx.customers_id + '-' + modal.customerSelector.key"
                    :where="conditionCustomersActive" />
        <div class="sc-customer-modal-footer">
          <b-button variant="outline-secondary" @click="modal.customerSelector.active = false">
            <b-icon icon="x" class="mr-1"></b-icon>Cancelar
          </b-button>
        </div>
      </b-modal>

      <!-- ── MODAL: Concepto rápido ── -->
      <b-modal v-model="modal.quickConcept.active"
               no-close-on-backdrop
               no-close-on-esc
               size="sm"
               @shown="focusQuickConceptDescription">
        <template #modal-header>
          <div class="app-modal-header">
            <span class="app-modal-header__icon"><AppIcon name="list-ul" /></span>
            <h5 class="app-modal-header__title">Agregar concepto</h5>
          </div>
        </template>

        <b-form-group label="Descripción">
          <app-text-input ref="quickConceptDescription"
                          v-model.trim="modal.quickConcept.description"
                          size="lg"
                          autocomplete="off"
                          maxlength="255"
                          @keydown.enter.prevent="focusQuickConceptPrice"
                          @keydown.esc.prevent="closeQuickConcept" />
        </b-form-group>

        <b-form-group label="Precio final">
          <b-input-group prepend="$">
            <app-number-input ref="quickConceptPrice"
                              v-model="modal.quickConcept.priceInput"
                              size="lg"
                              :decimals="2"
                              @keydown.enter.prevent="saveQuickConcept"
                              @keydown.esc.prevent="closeQuickConcept" />
          </b-input-group>
        </b-form-group>

        <div class="sc-shortcuts mt-3">
          <span class="sc-shortcut"><kbd>Enter</kbd> Siguiente / Guardar</span>
          <span class="sc-shortcut"><kbd>Esc</kbd> Cancelar</span>
        </div>

        <template #modal-footer>
          <b-button variant="outline-secondary" @click="closeQuickConcept">
            <b-icon icon="x" class="mr-1"></b-icon>Cancelar
          </b-button>
          <b-button variant="success" @click="saveQuickConcept">
            <b-icon icon="check2" class="mr-1"></b-icon>Guardar
          </b-button>
        </template>
      </b-modal>

      <!-- ── MODAL: Eliminar por código ── -->
      <b-modal v-model="deleteByCode.show"
               no-close-on-backdrop
               size="lg" @shown="$refs.deleteCodeInput && $refs.deleteCodeInput.focus()">
        <template #modal-header>
          <div class="app-modal-header">
            <span class="app-modal-header__icon"><AppIcon name="upc-scan" /></span>
            <h5 class="app-modal-header__title">Eliminar producto</h5>
          </div>
        </template>

        <p class="text-muted mb-3" style="font-size:1.05rem;">
          Escaneá o tipeá el código del artículo que el cliente devuelve.
        </p>

        <b-input-group size="lg">
          <b-input-group-prepend>
            <b-input-group-text style="font-size:1.2rem;"><b-icon icon="upc-scan"></b-icon></b-input-group-text>
          </b-input-group-prepend>
          <b-form-input ref="deleteCodeInput"
                        v-model="deleteByCode.buffer"
                        placeholder="Código de barras..."
                        style="font-size:1.3rem;"
                        autocomplete="off"
                        @keydown.enter.prevent="confirmDeleteByCode"
                        @keydown.esc="deleteByCode.show = false"
                        @input="onDeleteCodeInput" />
        </b-input-group>

        <b-alert v-if="deleteByCode.notFound" show variant="warning" class="mt-3 mb-0" style="font-size:1.05rem;">
          <b-icon icon="exclamation-circle" class="mr-1"></b-icon>
          No se encontró ese artículo en la venta actual.
        </b-alert>

        <div class="sc-shortcuts mt-3">
          <span class="sc-shortcut"><kbd>F10</kbd> Cerrar sin cambios</span>
        </div>

        <template #modal-footer>
          <b-button size="lg" variant="outline-secondary" @click="deleteByCode.show = false">
            <b-icon icon="x" class="mr-1"></b-icon> Cancelar (F10)
          </b-button>
        </template>
      </b-modal>


      <!-- ── MODAL: Errores de cola ── -->
      <b-modal v-model="modal.queueErrors.active"
               hide-header-close header-bg-variant="danger"
               header-text-variant="white" size="lg" scrollable>
        <div slot="modal-header">
          <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
          Cola de comprobantes
        </div>
        <b-alert show :variant="queueStats.failed ? 'warning' : 'info'">
          Pendientes: <strong>{{ queueStats.pending }}</strong> ·
          Guardando: <strong>{{ queueStats.processing }}</strong> ·
          Con error: <strong>{{ queueStats.failed }}</strong> ·
          Total sin sincronizar: <strong>{{ formatCurrency(queueOpenTotal) }}</strong>
        </b-alert>
        <div v-if="!modal.queueErrors.items.length" class="text-muted text-center py-3">
          No hay comprobantes pendientes de sincronización.
        </div>
        <div v-if="modal.queueErrors.recentDone.length && !modal.queueErrors.items.length" class="mb-3">
          <div class="small font-weight-bold text-muted mb-2">Últimas sincronizadas</div>
          <div class="sc-cierre-cpbt-list">
            <div class="sc-cierre-cpbt-header">
              <span>Hora</span>
              <span>ID</span>
              <span>Fiscal</span>
              <span>Impresión</span>
              <span class="text-right">Total</span>
            </div>
            <div v-for="item in modal.queueErrors.recentDone" :key="'done-' + item.id"
                 class="sc-cierre-cpbt-row">
              <span class="text-muted">{{ formatDateTimeHM(item.syncedAt) }}</span>
              <span>{{ item.savedId || '—' }}</span>
              <span>
                <b-badge :variant="queueFiscalVariant(item.fiscalStatus)">
                  {{ queueFiscalLabel(item.fiscalStatus) }}
                </b-badge>
              </span>
              <span>
                <b-badge :variant="queuePrintVariant(item.printStatus)">
                  {{ queuePrintLabel(item.printStatus) }}
                </b-badge>
              </span>
              <span class="sc-cierre-cpbt-total">{{ formatCurrency(item.total) }}</span>
            </div>
          </div>
        </div>
        <div v-for="(item, idx) in modal.queueErrors.items" :key="item.id" class="mb-3">
          <b-card header-bg-variant="light" :border-variant="item.status === 'failed' ? 'danger' : 'warning'">
            <template #header>
              <div class="d-flex justify-content-between align-items-center flex-wrap">
                <span>
                  <strong>Factura {{ idx + 1 }}</strong>
                  — {{ item.customerName }}
                  <b-badge variant="secondary" class="ml-1">{{ item.itemsCount }} ítem(s)</b-badge>
                  <b-badge :variant="queueStatusVariant(item.status)" class="ml-1">
                    {{ queueStatusLabel(item.status) }}
                  </b-badge>
                  <b-badge :variant="queueSyncStageVariant(item.syncStage)" class="ml-1">
                    {{ queueSyncStageLabel(item.syncStage) }}
                  </b-badge>
                </span>
                <small class="text-muted">{{ item.enqueuedAt }}</small>
              </div>
            </template>
            <div class="small mb-2">
              <strong>Total:</strong> {{ formatCurrency(item.total) }}
              <span v-if="item.cashNames"> · <strong>Caja:</strong> {{ item.cashNames }}</span>
              <span v-if="item.lastAttemptAt"> · <strong>Último intento:</strong> {{ formatDateTimeHM(item.lastAttemptAt) }}</span>
              <span v-if="item.lastErrorAt"> · <strong>Último error:</strong> {{ formatDateTimeHM(item.lastErrorAt) }}</span>
              <span v-if="item.stale" class="text-danger"> · Trabado</span>
            </div>
            <div class="small mb-2">
              <strong>Estado:</strong>
              {{ queueSyncStageLabel(item.syncStage) }} ·
              Fiscal: {{ queueFiscalLabel(item.fiscalStatus) }} ·
              Impresión: {{ queuePrintLabel(item.printStatus) }}
            </div>
            <div class="d-flex flex-wrap mb-2" style="gap:6px">
              <b-button size="sm" variant="outline-secondary" @click="toggleQueueItemDetail(item.id)">
                <b-icon icon="list-ul" class="mr-1"></b-icon>
                {{ modal.queueErrors.expandedId === item.id ? 'Ocultar detalle' : 'Ver detalle' }}
              </b-button>
              <b-button size="sm" variant="outline-secondary" @click="copyQueueItemData(item)">
                <b-icon icon="clipboard" class="mr-1"></b-icon>Copiar datos
              </b-button>
            </div>
            <div v-if="modal.queueErrors.expandedId === item.id" class="mb-2">
              <div class="small text-muted mb-2">
                <span v-if="item.pointSale"><strong>Punto de venta:</strong> {{ item.pointSale }}</span>
                <span v-if="item.customerDocument"> · <strong>Doc:</strong> {{ item.customerDocument }}</span>
                <span v-if="item.idempotencyKey"> · <strong>Key:</strong> {{ item.idempotencyKey }}</span>
              </div>
              <div class="sc-cierre-cpbt-list">
                <div class="sc-cierre-cpbt-header">
                  <span>Producto</span>
                  <span>Cant.</span>
                  <span class="text-right">Importe</span>
                </div>
                <div v-for="(product, productIdx) in item.products" :key="item.id + '-product-' + productIdx"
                     class="sc-cierre-cpbt-row">
                  <span>
                    {{ product.name }}
                    <small v-if="product.code" class="text-muted d-block">{{ product.code }}</small>
                  </span>
                  <span>{{ product.quantity }}</span>
                  <span class="sc-cierre-cpbt-total">{{ formatCurrency(product.total) }}</span>
                </div>
              </div>
            </div>
            <pre v-if="item.error" class="mb-2 sales-counter-queue-error-pre">{{ item.error }}</pre>
            <div v-if="item.status === 'failed'" class="d-flex flex-wrap" style="gap:6px">
              <b-button size="sm" variant="outline-secondary" @click="recoverToForm(item)">
                <b-icon icon="pencil-square" class="mr-1"></b-icon>Editar en formulario
              </b-button>
              <b-button size="sm" variant="warning" @click="retryQueueItem(item.id)">
                <b-icon icon="arrow-repeat" class="mr-1"></b-icon>Reintentar ésta
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="confirmDiscardQueueItem(item)">
                <b-icon icon="trash" class="mr-1"></b-icon>Descartar
              </b-button>
            </div>
          </b-card>
        </div>
        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1"
                    @click="modal.queueErrors.active = false">Cerrar</b-button>
          <b-button variant="outline-dark" class="mr-1" @click="syncQueueNow()">
            <b-icon icon="arrow-repeat" class="mr-1"></b-icon>Sincronizar ahora
          </b-button>
          <b-button variant="outline-warning" class="mr-1"
                    :disabled="!queueStats.staleProcessing"
                    @click="recoverQueueStuck()">
            <b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>Recuperar trabados
          </b-button>
          <b-button variant="danger" :disabled="!queueStats.failed" @click="retryQueue()">
            <b-icon icon="arrow-repeat" class="mr-1"></b-icon>Reintentar errores
          </b-button>
        </div>
      </b-modal>

      <!-- ── RESUMEN DE VENTA (CONFIRMACIÓN FINAL) ── -->
      <b-modal v-model="modal.confirm.active"
               title="Resumen de venta"
               size="xl" centered
               no-close-on-backdrop no-close-on-esc
               hide-footer
               @shown="$refs.inputObservaciones && $refs.inputObservaciones.focus()">
        <div class="sc-confirm-modal">
          <b-alert v-if="isDeclaredPointSale" show variant="primary" class="mb-3">
            <b-icon icon="cloud-check" class="mr-1"></b-icon>
            Esta venta se va a emitir como <strong>factura electrónica</strong> con el punto de venta
            <strong>{{ pointSaleLabel }}</strong>.
          </b-alert>

          <!-- Contexto de la venta -->
          <div class="sc-confirm-context mb-3">
            <span v-if="ctx.customer">
              <b-icon icon="person-fill" class="mr-1"></b-icon>{{ ctx.customer.name }}
            </span>
            <span v-if="ctx.points_sales_id">
              <b-icon icon="shop-window" class="mr-1"></b-icon>{{ pointSaleLabel }}
            </span>
            <span v-if="ctx.price_list_id">
              <b-icon icon="tags-fill" class="mr-1"></b-icon>{{ priceListLabel }}
            </span>
            <span v-if="ctx.defaultMethodsPaymentId">
              <b-icon icon="credit-card" class="mr-1"></b-icon>{{ defaultMethodsPaymentLabel }}
            </span>
          </div>

          <!-- Columnas: productos | cobro -->
          <div class="sc-confirm-columns mb-3">

            <!-- Izquierda: productos -->
            <div class="sc-confirm-col">
              <div class="sc-confirm-section-label">Productos</div>
              <div class="sc-confirm-products sc-confirm-products-scroll">
                <div v-for="item in items" :key="item._uid" class="sc-confirm-product-row">
                  <span class="sc-confirm-product-name">{{ item.name }}</span>
                  <span class="sc-confirm-product-qty">× {{ item.quantity }}</span>
                  <span class="sc-confirm-product-price">{{ formatCurrency(item.price * item.quantity) }}</span>
                </div>
              </div>
            </div>

            <!-- Derecha: cobro + total -->
            <div class="sc-confirm-col sc-confirm-col-right">
              <div class="sc-confirm-section-label">Cobro</div>
              <div class="sc-confirm-products mb-3">
                <div v-for="line in paymentLines.filter(l => l.selected)" :key="line.caja.id" class="sc-confirm-product-row">
                  <span class="sc-confirm-product-name">{{ line.caja.name }}</span>
                  <span class="sc-confirm-product-price">{{ formatCurrency(line.amount) }}</span>
                </div>
              </div>
              <div v-if="generalDiscountCents" class="sc-confirm-discount-row">
                <span>Descuento general ({{ formatDiscountPercentage(generalDiscountPercentage) }}%)</span>
                <strong>−{{ formatCurrency(generalDiscountAmount) }}</strong>
              </div>
              <div class="sc-confirm-total-row">
                <span>Total</span>
                <span class="sc-confirm-total-amount">{{ formatCurrency(total) }}</span>
              </div>
            </div>

          </div>

          <!-- Observaciones internas (ancho completo) -->
          <div class="sc-confirm-section-label">Observaciones internas</div>
          <app-textarea ref="inputObservaciones"
                           v-model="modal.confirm.observations"
                           rows="2" max-rows="4"
                           placeholder="Opcional — se guarda en observaciones internas del comprobante"
                           class="mb-4" />

          <!-- Botones -->
          <div class="d-flex justify-content-between align-items-center">
            <b-button variant="outline-secondary" @click="modal.confirm.active = false" :disabled="isSaving">
              <b-icon icon="arrow-left" class="mr-1"></b-icon>Volver
              <span class="sc-kbd-hint ml-2">ESC</span>
            </b-button>
            <div class="d-flex align-items-center sc-confirm-actions">
              <b-button variant="outline-primary" size="lg" class="mr-2"
                        @click="doConfirmPayment(true)" :disabled="isSaving">
                <b-icon icon="printer" class="mr-1"></b-icon>
                Cobrar e imprimir
                <span class="sc-kbd-hint ml-2">F9</span>
              </b-button>
              <b-button variant="success" size="lg" @click="doConfirmPayment(false)" :disabled="isSaving" class="sc-btn-cobrar">
                <b-spinner v-if="isSaving" small class="mr-1"></b-spinner>
                <b-icon v-else icon="check-circle" class="mr-1"></b-icon>
                Cobrar sin imprimir
                <span class="sc-kbd-hint sc-kbd-hint-light ml-2">F12</span>
              </b-button>
            </div>
          </div>

        </div>
      </b-modal>

      <!-- ── CALCULADORA DE VUELTO ── -->
      <b-modal v-model="modal.vuelto.active"
               title="Calculadora de vuelto"
               hide-footer centered no-close-on-esc
               @shown="$refs.inputEntrega && $refs.inputEntrega.$el.focus()">
        <div class="text-center mb-4">
          <div class="text-muted mb-1" style="font-size:1.15rem">Total a cobrar</div>
          <div style="font-size:2.8rem;font-weight:700;line-height:1.1">{{ formatCurrency(total) }}</div>
        </div>
        <b-form-group label="El cliente entrega $" label-class="font-weight-bold" label-size="lg">
          <AppNumberInput ref="inputEntrega"
                          v-model="paymentEntrega"
                          min="0" placeholder="0,00"
                          size="lg"
                          style="font-size:2.2rem;font-weight:700;height:auto;text-align:right" />
        </b-form-group>
        <div v-if="paymentEntrega > 0"
             class="text-center py-4 rounded"
             :style="vuelto >= 0 ? 'background:#d4edda;color:#155724' : 'background:#f8d7da;color:#721c24'">
          <div class="mb-1" style="font-size:1.25rem;font-weight:600">{{ vueltoLabel }}</div>
          <div style="font-size:3.6rem;font-weight:700;line-height:1.1">{{ formatCurrency(Math.abs(vuelto)) }}</div>
        </div>
      </b-modal>

      <!-- ── PRINTER ── -->
      <Printer v-if="crud.print.id && crud.print.mode == 'pdf'"
               :reference="crud.print.reference"
               :registerID="crud.print.id"
               :key="'pdf-' + crud.print.key"
               :manyFormat="true" />

      <EscposPrinter v-if="crud.print.id && crud.print.mode == 'thermal_printer'"
                     :reference="crud.print.reference"
                     :registerID="crud.print.id"
                     :key="'thermal-printer-' + crud.print.key"
                     :manyFormat="true"
                     :direct="true" />

      <EscposPrinter v-if="localPrint.content || localPrint.payload"
                     :content="localPrint.content"
                     :print-payload="localPrint.payload"
                     :key="'local-ticket-' + localPrint.key"
                     :direct="true"
                     @printed="onLocalTicketPrinted"
                     @error="onLocalTicketError" />

      <!-- ── Toast propio de Venta Mostrador ── -->
      <div class="sc-toast-wrap">
        <transition-group name="sc-toast" tag="div">
          <div v-for="t in scToasts" :key="t.id"
               class="sc-toast-item" :class="`sc-toast-${t.type}`"
               v-html="t.message" />
        </transition-group>
      </div>

    </b-col>
  </b-row>
  </div>
</template>

<script>
  import serviceAPI from './services'
  import cpbtDetailService, { enableDetailCache, disableDetailCache, clearDetailCache } from '@/components/inc/cpbtDetail/services'
  import saleQueue from './salesCounter/queue'
  import catalogStorage from './salesCounter/catalogStorage'
  import contextStorage from './salesCounter/contextStorage'
  import Printer from '@/components/inc/printer/printer'
  import CashSession from './inc/cashSession'
  import EscposPrinter from '@/components/inc/printer/escposPrinter'
  import FindObject from '@/components/inc/find/findObject'
  import AppNumberInput from '@/components/inc/input/AppNumberInput'
  import Helper from '@/handler/helper'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Param from '@/config/parameters'
  import localTicketCache from '@/components/inc/printer/localTicketCache'
  import moment from 'moment'

  const CONNECTIVITY_CHECK_INTERVAL_MS = 5000
  const API_CHECK_TIMEOUT_MS = 3500
  // Cambiar este valor cuando cambie la estructura o el criterio de precios
  // almacenados offline. Obliga a refrescar el catálogo ya existente.
  const CATALOG_PRICE_FORMAT_VERSION = 2

  export default {
    name: 'SalesCounter',
    components: { Printer, EscposPrinter, FindObject, AppNumberInput, CashSession },

    data: () => ({
      access: {
        module_id: Modules.VENTAS,
        profile_id: Profiles.PERSONAL,
        view_reference: 'salesCounter',
      },
      parameters: {
        freeAddTypeOperation: Helper.hasParametersAccess(Param.P61),
      },

      // Contexto fijo para toda la sesión (consumidor final + lista + punto de venta)
      ctx: {
        ready: false,
        customers_id: 0,
        customer: null,
        price_list_id: 0,
        price_list: null,
        points_sales_id: 0,
        points_sales: null,
        types_vouchers_id: 0,
        types_vouchers: null,
        defaultMethodsPaymentId: 0,
        defaultMethodsPayment: null,
      },

      configurations: null,
      finances: { staff: null },
      contextSelection: { pointSaleId: 0 },
      quickConcept: null,

      // Productos escaneados
      items: [],
      selectedItem: null,
      generalDiscount: { percentage: 0, percentageInput: '0' },
      tableFields: [
        { key: 'idx',      label: '#',        thStyle: 'width:44px',  class: 'text-center' },
        { key: 'name',     label: 'Producto' },
        { key: 'quantity', label: 'Cant.',    thStyle: 'width:110px', class: 'text-center' },
        { key: 'subtotal', label: 'Precio',   thStyle: 'width:140px', class: 'text-right' },
        { key: 'actions',  label: '',         thStyle: 'width:50px',  class: 'text-center' },
      ],

      // Barcode
      barcode: { buffer: '', searching: false, blocked: false, _autoTimer: null, queue: [] },

      // Pago
      paymentShow: false,
      paymentLines: [],
      paymentEntrega: '',
      isSaving: false,

      // Impresión
      crud: { print: { reference: '', id: 0, key: 0, mode: 'pdf' } },
      localPrint: { content: '', payload: null, key: 0, reference: 'invoice-nd' },

      // Cola
      queueStats: { total: 0, pending: 0, processing: 0, failed: 0, done: 0 },
      modal: {
        queueErrors: { active: false, items: [], recentDone: [], expandedId: null },
        customerSelector: { active: false, key: 0 },
        customerSelectorShow: false,
        vuelto: { active: false },
        confirm: { active: false, observations: '' },
        quickConcept: { active: false, description: '', priceInput: 0 },
      },

      // Reloj para refrescar etiquetas relativas ("hace 20 min")
      clockNow: Date.now(),

      // Prefetch / offline
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      internetStatus: { online: null, checking: false, lastCheckedAt: null, error: '' },
      apiStatus: { online: null, checking: false, lastCheckedAt: null, error: '' },
      offlineStorage: { ready: null, checking: false, error: '' },
      prefetch: { active: false, done: false },
      offlineDefaultContext: null,
      offlineContextForced: { active: false, appliedAt: null },
      // Punto de venta elegido manualmente: dura mientras se permanezca en la pantalla.
      pointSaleOverride: null,
      pointSaleRestorePending: false,
      offlineCatalog: {
        status: 'unknown',
        productsCount: 0,
        updatedAt: null,
        priceListId: 0,
        priceFormatVersion: 0,
        error: '',
        progress: { step: '', current: 0, total: 0, percent: 0, message: '' },
      },

      // Eliminar por código
      deleteByCode: { show: false, buffer: '', notFound: false, _autoTimer: null },

      // Búsqueda por nombre / categoría
      productSearch: { active: false, query: '', results: [], show: false, loading: false, activeIdx: -1, _timer: null },

      focusMode: false,
      scanScreenMinHeight: 0,
      scToasts: [],

      // Caja
      arr: { financesCash: [], ivaConditions: [], financesCashAll: [], pointsSales: [] },
      // El resto del estado de caja vive en CashSession.
      cajaFlow: {
        show: false,
        loaded: false,
        totalCajas: null,
        // Reconexión con un ticket en curso: la caja se re-verifica al terminarlo.
        recheckPending: false,
      },
    }),

    watch: {
      async 'ctx.ready'(val) {
        if (!val) return
        await this._loadProductCacheMeta()
        if (!this._isCacheStale()) {
          this.prefetch.done = true
        }
        this._startPrefetch()
      },
      ctxErrors(errors) {
        if (errors.length && this.modal.queueErrors.active) {
          this.modal.queueErrors.active = false
        }
      },
      cajaRecheckBlocked(blocked) {
        if (!blocked && this.cajaFlow.recheckPending) this.requestCajaRecheck()
      },
      'apiStatus.online'(online, wasOnline) {
        saleQueue.setOnline(online === true)
        // Al volver la conexión se re-verifica la caja: offline se facturó sin
        // chequearla y pudo quedar cerrada (o cerrarse desde otra terminal).
        if (online === true && wasOnline === false) this.requestCajaRecheck()
        if (online === true) {
          this.loadAvailablePointSales()
          saleQueue.retryFailed()
          saleQueue.startProcessing()
        }
      },
    },

    created() {
      Helper.hasAccessView(this.access)
    },
    destroyed() {
      window.removeEventListener('keydown', this.handlerKey)
      window.removeEventListener('resize', this._updateLayout)
      document.removeEventListener('fullscreenchange', this._onFullscreenChange)
      if (this._sidebarObs) { this._sidebarObs.disconnect(); this._sidebarObs = null }
      this.stopConnectivityWatch()
      this.stopClockTick()
      this.disableFocusMode()
      this.exitBrowserFullscreen()
    },
    mounted() {
      this._toastSeq = 0
      this.loadConfigurations()
      this._updateLayout()
      const sb = document.querySelector('.c-sidebar')
      if (sb && window.ResizeObserver) {
        this._sidebarObs = new ResizeObserver(this._updateLayout)
        this._sidebarObs.observe(sb)
      }
      window.addEventListener('resize', this._updateLayout)
      document.addEventListener('fullscreenchange', this._onFullscreenChange)
    },
    async activated() {
      // Recarga por deploy diferida: con un ticket a medio cargar o un cobro
      // abierto, main.js no recarga la app hasta que el mostrador quede libre.
      window.__appBusy = this.isAppBusyForUpdate
      this.disableFocusMode()
      this._updateLayout()
      window.addEventListener('keydown', this.handlerKey)
      enableDetailCache()
      this._queueUnsubscribe = saleQueue.onUpdate(stats => {
        this.queueStats = stats
        if (this.modal.queueErrors.active) {
          this.modal.queueErrors.items = saleQueue.getOpenItems()
          this.modal.queueErrors.recentDone = saleQueue.getRecentDoneItems()
        }
      })
      this._queueSavedUnsubscribe = saleQueue.onSaved((responseData, saleData) => {
        if (saleData && saleData.from_electronic_sale && saleData.print_ticket !== false) {
          this.sendPrinterSale(responseData)
        }
      })
      saleQueue.startProcessing()
      this.queueStats = saleQueue.getStats()
      this.loadIvaConditions()
      localTicketCache.warmCache(this.localPrint.reference)
      window.addEventListener('online',  this._onOnline)
      window.addEventListener('offline', this._onOffline)
      this.syncBrowserOnlineState()
      this.startConnectivityWatch()
      this.startClockTick()
      await this.reloadContext()
      // Si ctx ya estaba listo (vuelta a la pantalla), relanzar prefetch si la caché está vencida
      if (this.ctx.ready) {
        await this._loadProductCacheMeta()
        if (this._isCacheStale()) {
          this.prefetch.done = false
        } else {
          this.prefetch.done = true
        }
        this._startPrefetch()
      }
      const apiAvailable = await this.runConnectivityCheck({ silent: true })
      if (apiAvailable) this.loadAvailablePointSales()
      this.checkOfflineStorage()
      // Re-enfocar el input de barras cada vez que se vuelve a esta pantalla
      if (this.ctx.ready && !this.cajaFlow.show && !this.paymentShow) {
        this.$nextTick(() => this.focusBarcode())
      }
    },
    deactivated() {
      if (window.__appBusy === this.isAppBusyForUpdate) {
        window.__appBusy = null
      }
      disableDetailCache()
      clearDetailCache()
      if (this._queueUnsubscribe) this._queueUnsubscribe()
      if (this._queueSavedUnsubscribe) this._queueSavedUnsubscribe()
      window.removeEventListener('online',  this._onOnline)
      window.removeEventListener('offline', this._onOffline)
      this.stopConnectivityWatch()
      this.stopClockTick()
      window.removeEventListener('keydown', this.handlerKey)
      document.removeEventListener('fullscreenchange', this._onFullscreenChange)
      this.disableFocusMode()
      this.exitBrowserFullscreen()
      if (this._audioCtx) { this._audioCtx.close(); this._audioCtx = null }
    },

    computed: {
      customersDefault() {
        const config = this.getConfiguration(2)
        return config ? parseInt(config.value_number || 0) : 0
      },
      salesCounterPriceListDefault() {
        const config = this.getConfiguration(16)
        return config ? parseInt(config.value_number || 0) : 0
      },
      salesCounterMethodPaymentDefault() {
        const config = this.getConfiguration(17)
        return config ? parseInt(config.value_number || 0) : 0
      },
      salesCounterQuickConceptDefault() {
        const config = this.getConfiguration(18)
        return config ? parseInt(config.value_number || 0) : 0
      },
      weighedBarcodeFormats() {
        const config = this.getConfiguration(19)
        if (!config || !config.value_string) return []
        try {
          const formats = JSON.parse(config.value_string)
          return Array.isArray(formats) ? formats.filter(format => format && format.enabled !== false) : []
        } catch(e) {
          return []
        }
      },
      isQuickConceptShortcutActive() {
        return String(this.productSearch.query || '').trim().startsWith('**')
      },
      quickConceptShortcutPrice() {
        if (!this.isQuickConceptShortcutActive) return 0
        const rawPrice = String(this.productSearch.query || '').trim().slice(2).trim()
        return this.centsToMoney(this.moneyToCents(this.parseMoneyInput(rawPrice)))
      },
      apiOnline() {
        return this.apiStatus.online === true
      },
      connectionStatus() {
        if (this.apiStatus.online === false) return 'offline'
        if (this.apiStatus.online === true) return 'online'
        return 'unknown'
      },
      connectionStatusLabel() {
        switch (this.connectionStatus) {
          case 'online':
            return 'Online'
          case 'offline':
            return 'Sin conexión'
          default:
            return 'Conexión sin verificar'
        }
      },
      connectionStatusMessage() {
        switch (this.connectionStatus) {
          case 'offline':
            return 'Podés seguir facturando. Los comprobantes quedan guardados localmente y se sincronizan cuando vuelva la conexión.'
          case 'unknown':
            return 'Todavía no se comprobó la conexión con el servidor.'
          default:
            return 'Conectado al servidor. Los comprobantes se sincronizan normalmente.'
        }
      },
      connectionStatusIcon() {
        switch (this.connectionStatus) {
          case 'online':
            return 'wifi'
          case 'offline':
            return 'wifi-off'
          default:
            return 'question-circle'
        }
      },
      connectionStatusVariant() {
        switch (this.connectionStatus) {
          case 'online':
            return 'success'
          case 'offline':
            return 'danger'
          default:
            return 'secondary'
        }
      },
      connectionStatusTitle() {
        let lastCheckedAt = this.internetStatus.lastCheckedAt || this.apiStatus.lastCheckedAt
        let checked = lastCheckedAt
          ? ' Última verificación: ' + moment(lastCheckedAt).format('DD/MM HH:mm:ss') + '.'
          : ''

        return this.connectionStatusMessage + checked
      },
      canChangeOnlineContext() {
        return this.ctx.ready && this.apiOnline && !this.paymentShow && !this.cajaFlow.show
      },
      conditionCustomersActive(){
        return [{field: 'active', condition: true}]
      },
      isDeclaredPointSale() {
        return this.pointSaleIsDeclared(this.ctx.points_sales)
      },
      isNdPointSale() {
        const point = this.ctx.points_sales
        return !!point && (point.nd === true || point.nd === 1 || point.nd === '1')
      },
      pointSaleOptions() {
        const list = this.availablePointSales.map(point => ({
          value: point.id,
          text: this.formatPointSaleLabel(point),
        }))
        if (!list.length) {
          return [{ value: 0, text: 'Sin puntos de venta declarados disponibles', disabled: true }]
        }
        return list
      },
      availablePointSales() {
        const points = []
        const addPoint = point => {
          if (!point || !point.id) return
          if (points.find(item => parseInt(item.id) === parseInt(point.id))) return
          points.push(point)
        }

        addPoint(this.finances.staff && this.finances.staff.point_sale)
        const declaredPoints = this.arr.pointsSales || []
        declaredPoints.forEach(point => {
          if (point.active && this.pointSaleIsDeclared(point) && point.status) {
            addPoint(point)
          }
        })

        return points
      },
      ctxErrors() {
        if (!this.ctx.ready) return []
        const errors = []
        if (this.cajaFlow.loaded && this.cajaFlow.totalCajas === 0) {
          errors.push({
            icon: 'cash-stack',
            variant: 'danger',
            title: 'No hay cajas creadas',
            detail: 'El mostrador necesita al menos una caja para poder cobrar. Creá una caja en Finanzas → Cajas antes de facturar.',
          })
          return errors
        }
        if (!this.customersDefault) {
          errors.push({
            icon: 'person-x-fill',
            variant: 'danger',
            title: 'No hay cliente por defecto configurado',
            detail: 'El mostrador necesita un cliente por defecto para funcionar. Configuralo en Administración → Configuración → Venta Mostrador.',
          })
          return errors
        }
        if (!this.ctx.customer) {
          errors.push({
            icon: 'person-exclamation-fill',
            variant: 'danger',
            title: 'No se pudo cargar el cliente por defecto',
            detail: `El cliente configurado (ID: ${this.customersDefault}) no existe o no se pudo cargar. Verificá la configuración del mostrador.`,
          })
          return errors
        }
        if (!this.ctx.points_sales_id) {
          errors.push({
            icon: 'shop-window',
            variant: 'warning',
            title: 'El staff no tiene punto de venta asignado',
            detail: 'Venta Mostrador toma el punto de venta del staff. Configurá un punto de venta en el perfil del staff o elegí uno declarado con conexión a la API.',
          })
        }
        if (this.ctx.points_sales_id && this.ctx.points_sales && this.ctx.points_sales.active === false) {
          errors.push({
            icon: 'shop-window',
            variant: 'danger',
            title: 'El punto de venta está inactivo',
            detail: `El punto de venta ${this.pointSaleLabel} no está activo. Activá el punto de venta o elegí otro antes de facturar.`,
          })
        }
        if (!this.ctx.price_list_id) {
          errors.push({
            icon: 'tags-fill',
            variant: 'warning',
            title: 'No hay lista de precios para venta mostrador',
            detail: 'Configurá una lista de precios en Administración → Ventas → Configuraciones. Sin esto no se pueden buscar ni agregar productos.',
          })
        }
        if (!this.ctx.defaultMethodsPaymentId) {
          errors.push({
            icon: 'credit-card',
            variant: 'warning',
            title: 'No hay método de pago para venta mostrador',
            detail: 'Configurá un método de pago por defecto en Administración → Ventas → Configuraciones. Sin esto no se puede generar el comprobante.',
          })
        }
        if (this.ctx.points_sales_id && this.isDeclaredPointSale && !this.apiOnline) {
          errors.push({
            icon: 'wifi-off',
            variant: 'danger',
            title: 'El punto de venta declarado requiere conexión',
            detail: `El punto de venta ${this.pointSaleLabel} informa comprobantes a ARCA. Para facturar con este punto de venta necesitás conexión activa con la API.`,
          })
        }
        if (this.ctx.points_sales_id && this.isDeclaredPointSale && this.ctx.points_sales && !this.ctx.points_sales.status) {
          errors.push({
            icon: 'cloud-slash',
            variant: 'danger',
            title: 'El punto de venta declarado no está listo',
            detail: `El punto de venta ${this.pointSaleLabel} no está conectado a ARCA. Conectalo desde la configuración de puntos de venta antes de facturar.`,
          })
        }
        if (this.hasDefaultMethodsPaymentSurcharge) {
          errors.push({
            icon: 'credit-card-2-front',
            variant: 'warning',
            title: 'El método de pago tiene recargo',
            detail: this.defaultMethodsPaymentSurchargeMessage,
          })
        }
        return errors
      },
      priceListLabel() {
        if (!this.ctx.price_list) return `#${this.ctx.price_list_id}`
        return this.ctx.price_list.name || this.ctx.price_list.reference || `#${this.ctx.price_list_id}`
      },
      pointSaleLabel() {
        if (!this.ctx.points_sales) return `#${this.ctx.points_sales_id}`
        const point = this.ctx.points_sales.point_sale || this.ctx.points_sales.number || this.ctx.points_sales.id || this.ctx.points_sales_id
        const name = this.ctx.points_sales.name || this.ctx.points_sales.reference || ''
        return name ? `${name} (${point})` : `#${point}`
      },
      defaultMethodsPaymentLabel() {
        if (!this.ctx.defaultMethodsPayment) return `#${this.ctx.defaultMethodsPaymentId}`
        return this.ctx.defaultMethodsPayment.name || `#${this.ctx.defaultMethodsPaymentId}`
      },
      defaultMethodsPaymentSurcharge() {
        return parseFloat((this.ctx.defaultMethodsPayment && this.ctx.defaultMethodsPayment.surcharge) || 0) || 0
      },
      hasDefaultMethodsPaymentSurcharge() {
        return this.defaultMethodsPaymentSurcharge > 0
      },
      defaultMethodsPaymentSurchargeMessage() {
        const method = this.defaultMethodsPaymentLabel
        return `El método de pago "${method}" tiene un recargo configurado de ${this.defaultMethodsPaymentSurcharge}%. Venta Mostrador todavía no calcula ni cobra recargos por método de pago, por eso no se puede facturar con este método. Cambiá el método de pago del cliente por uno sin recargo o usá la pantalla de ventas completa.`
      },
      reversedItems() {
        return [...this.items].reverse()
      },
      subtotal() {
        return this.centsToMoney(this.subtotalCents)
      },
      subtotalCents() {
        return this.items.reduce((s, i) => s + this.moneyToCents(Number(i.quantity) * Number(i.price)), 0)
      },
      generalDiscountPercentage() {
        return Number(this.generalDiscount.percentage || 0)
      },
      generalDiscountCents() {
        return Math.max(0, this.subtotalCents - this.totalCents)
      },
      generalDiscountAmount() {
        return this.centsToMoney(this.generalDiscountCents)
      },
      totalCents() {
        return Math.max(0, Math.round(this.subtotalCents * (100 - this.generalDiscountPercentage) / 100))
      },
      total() {
        return this.centsToMoney(this.totalCents)
      },
      itemsCount() {
        return this.items.reduce((s, i) => s + Number(i.quantity), 0)
      },
      totalWithSurcharge() {
        return this.total
      },
      totalWithSurchargeCents() {
        return this.totalCents
      },
      preferredPaymentLine() {
        const idx = this.paymentLines.findIndex(l => l.preferred)
        return idx >= 0 ? { ...this.paymentLines[idx], _idx: idx } : null
      },
      otherPaymentLines() {
        return this.paymentLines
          .map((l, i) => ({ ...l, _idx: i }))
          .filter(l => !l.preferred)
      },
      selectedPaymentTotal() {
        return this.centsToMoney(this.selectedPaymentTotalCents)
      },
      selectedPaymentTotalCents() {
        return this.paymentLines.filter(l => l.selected).reduce((s, l) => s + this.moneyToCents(l.amount), 0)
      },
      paymentDiffCents() {
        return this.selectedPaymentTotalCents - this.totalWithSurchargeCents
      },
      paymentDiff() {
        return this.centsToMoney(this.paymentDiffCents)
      },
      paymentDiffTitle() {
        return this.paymentDiff < 0 ? 'Falta imputar' : 'Monto en exceso'
      },
      vuelto() {
        return this.centsToMoney(this.moneyToCents(this.paymentEntrega) - this.totalWithSurchargeCents)
      },
      vueltoLabel() {
        if (this.vuelto < 0) return 'Falta para cubrir el total'
        if (this.vuelto > 0) return 'Vuelto'
        return 'Importe exacto, sin vuelto'
      },
      canConfirm() {
        const sel = this.paymentLines.filter(l => l.selected)
        return sel.length > 0 && this.paymentDiffCents === 0
      },
      assignedCashId() {
        const userCashId = this.finances.staff && this.finances.staff.cash_id
        const pointCashId = (this.ctx.points_sales || {}).cash_id
        return parseInt(userCashId || pointCashId || 0)
      },
      hasAssignedCash() {
        return this.assignedCashId > 0
      },
      cashPointSaleNd() {
        if (this.parameters.freeAddTypeOperation || !this.ctx.points_sales) return null
        return this.ctx.points_sales.nd
      },
      // Ticket en curso: no se interrumpe con la pantalla de caja.
      cajaRecheckBlocked() {
        return !!(this.items.length || this.paymentShow || this.modal.confirm.active || this.isSaving)
      },
      queueOpenTotal() {
        return parseFloat(this.queueStats.openTotal || 0)
      },
      queueLastSyncLabel() {
        if (!this.queueStats.lastSyncAt) return ''
        this.clockNow // eslint-disable-line no-unused-expressions -- fuerza recálculo periódico
        return moment(this.queueStats.lastSyncAt).fromNow()
      },
      queueLastSyncFullLabel() {
        if (!this.queueStats.lastSyncAt) return ''
        return moment(this.queueStats.lastSyncAt).format('DD/MM/YYYY HH:mm:ss')
      },
      catalogStatusLabel() {
        if (this.offlineCatalog.status === 'updating') return 'actualizando'
        if (this.offlineCatalog.status === 'ready') return 'listo'
        if (this.offlineCatalog.status === 'error') return 'con error'
        if (this.offlineCatalog.status === 'empty') return 'sin datos'
        return 'sin verificar'
      },
      catalogStatusIcon() {
        if (this.offlineCatalog.status === 'ready') return 'cloud-check'
        if (this.offlineCatalog.status === 'error') return 'exclamation-triangle-fill'
        return 'cloud-arrow-down'
      },
      catalogUpdatedLabel() {
        if (!this.offlineCatalog.updatedAt) return ''
        return moment(this.offlineCatalog.updatedAt).format('DD/MM HH:mm')
      },
    },

    methods: {
      // El mostrador está "ocupado" si hay un ticket con productos o un cobro
      // abierto: main.js consulta esto antes de recargar la app por un deploy.
      isAppBusyForUpdate() {
        return this.items.length > 0 || !!this.paymentShow
      },


      // ── Keyboard ─────────────────────────────────────────────────────────────
      handlerKey(e) {
        // Un código no encontrado requiere confirmación explícita. Mientras el
        // aviso está abierto no se acepta ningún atajo ni otro escaneo.
        if (this.barcode.blocked) return

        // El resumen está por encima de la pantalla de cobro. Esc debe cerrar
        // primero este modal; de otro modo el atajo llega a la vista de fondo
        // y puede retroceder el cobro mientras el resumen sigue visible.
        if (this.modal.confirm.active && e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          if (!this.isSaving) this.modal.confirm.active = false
          return
        }

        // Calculadora de vuelto (F8). Mientras está abierta solo responde a
        // F8/Esc para cerrarla: el resto de los atajos (F12, Ctrl+número, Esc
        // de la pantalla de cobro) no debe actuar sobre el cobro de fondo.
        // El modal lleva no-close-on-esc: si se cerrara solo, al llegar acá ya
        // figuraría cerrado y Esc deseleccionaría las cajas del cobro.
        if (this.modal.vuelto.active) {
          if (e.key === 'F8' || e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            this.modal.vuelto.active = false
          } else if (/^F\d+$/.test(e.key)) {
            e.preventDefault()
          }
          return
        }
        if (e.key === 'F8') {
          e.preventDefault()
          if (this.paymentShow && !this.modal.confirm.active && !this.isSaving) this.openVuelto()
          return
        }

        if (e.key === 'F10') {
          e.preventDefault()
          if (this.deleteByCode.show) {
            this.deleteByCode.show = false
          } else if (!this.cajaFlow.show && !this.paymentShow && this.items.length) {
            this.deleteByCode.buffer  = ''
            this.deleteByCode.notFound = false
            this.deleteByCode.show    = true
          }
          return
        }
        if (this.deleteByCode.show) return
        if (this.modal.customerSelector.active) return
        if (this.modal.quickConcept.active) {
          if (e.key === 'Escape') {
            e.preventDefault()
            this.closeQuickConcept()
          }
          return
        }

        // Captura de chars del scanner cuando el barcode input (readonly) está enfocado
        const isBarcodeActive = this.$refs.barcodeInput &&
          document.activeElement === this.$refs.barcodeInput.$el
        if (isBarcodeActive && !this.productSearch.active && !this.paymentShow && !this.cajaFlow.show) {
          if (e.key === 'Enter') return
          if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
            e.preventDefault()
            this.barcode.buffer += e.key
            this.onBarcodeInput()
            return
          }
          // Teclas especiales (F3, F10, F12, etc.) caen al flujo normal abajo
        }

        if (e.key === 'F11') {
          e.preventDefault()
          this.toggleFocusMode()
          return
        }
        if (e.key === 'F3') {
          e.preventDefault()
          if (!this.cajaFlow.show && !this.paymentShow) {
            if (this.productSearch.active) {
              this.closeProductSearch()
            } else {
              this.openProductSearch()
            }
          }
          return
        }
        if (e.key === 'F9') {
          e.preventDefault()
          if (this.modal.confirm.active && !this.isSaving) {
            this.doConfirmPayment(true)
          }
          return
        }
        if (e.key === 'F12') {
          e.preventDefault()
          if (this.modal.confirm.active && !this.isSaving) {
            this.doConfirmPayment()
          } else if (this.paymentShow && this.canConfirm && !this.isSaving) {
            this.confirmPayment()
          } else if (!this.cajaFlow.show && !this.paymentShow && this.items.length && this.ctx.ready) {
            this.openPayment()
          }
          return
        }
        if (e.key === 'Escape') {
          if (this.paymentShow) this.paymentBack()
          return
        }
        // Ctrl+número: selecciona forma de pago
        if (this.paymentShow) {
          const isKeyNumber = e.code && (e.code.includes('Numpad') || e.code.includes('Digit'))
          if (isKeyNumber && e.ctrlKey && !e.shiftKey && !e.altKey) {
            e.preventDefault()
            const num = parseInt(e.code.slice(-1))
            if (!isNaN(num) && num >= 1 && num <= this.paymentLines.length) {
              this.toggleLine(num - 1)
            }
            return
          }
        }
        // Captura global de barcode: si no hay input con foco, acumular en buffer y procesar
        const tag = document.activeElement && document.activeElement.tagName
        if (!this.paymentShow && !this.cajaFlow.show && !this.deleteByCode.show && !isBarcodeActive &&
            tag !== 'INPUT' && tag !== 'TEXTAREA') {
          if (e.key === 'Enter') {
            e.preventDefault()
            this.processBarcode()
            return
          }
          if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
            e.preventDefault()
            this.barcode.buffer += e.key
            this.onBarcodeInput()
            this.$nextTick(() => { if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus() })
          }
        }
      },

      // ── Configuración ─────────────────────────────────────────────────────────
      resetContext() {
        this.ctx.ready = false
        this.ctx.customers_id = 0
        this.ctx.customer = null
        this.ctx.points_sales_id = 0
        this.ctx.points_sales = null
        this.ctx.price_list_id = 0
        this.ctx.price_list = null
        this.ctx.types_vouchers_id = 0
        this.ctx.types_vouchers = null
        this.ctx.defaultMethodsPaymentId = 0
        this.ctx.defaultMethodsPayment = null
        this.contextSelection.pointSaleId = 0
      },
      reloadContext() {
        // Al volver a la pantalla se arranca siempre con el punto de venta configurado.
        this.pointSaleOverride = null
        this.pointSaleRestorePending = false
        this.resetContext()
        return this.loadConfigurations()
      },
      getConfiguration(id) {
        return (this.configurations || []).find(config => parseInt(config.id) === parseInt(id)) || null
      },
      loadIvaConditions() {
        if (this.arr.ivaConditions && this.arr.ivaConditions.length) return Promise.resolve(this.arr.ivaConditions)
        return serviceAPI.obtenerCondicionesIva().then(response => {
          this.arr.ivaConditions = response.data || []
          return this.arr.ivaConditions
        }).catch(e => {
          console.warn('[salesCounter] iva conditions load error:', e)
          return []
        })
      },
      loadConfigurations() {
        return Promise.all([serviceAPI.obtenerConfiguracion(), this.loadIvaConditions()])
          .then(([response]) => {
            this.configurations = Array.isArray(response.data) ? response.data : []
            return this.loadConfiguredPriceList()
          })
          .then(() => this.showStaff())
          .then(() => this.loadConfiguredMethodPayment())
          .then(() => this.loadConfiguredQuickConcept())
          .then(() => {
            if (this.customersDefault) {
              return this.loadDefaultCustomer(this.customersDefault)
            }
            this.ctx.ready = true
            return null
          })
          .then(() => {
            this._captureOfflineDefaultContext()
            this.offlineContextForced.active = false
            this._persistCounterContext()
          })
          .catch(e => {
            console.warn('[salesCounter] configuration load error:', e)
            // Sin conexión (F5 offline): rehidratar el último contexto guardado
            // para poder seguir facturando offline.
            const restored = this._restoreCounterContext()
            if (!restored) {
              this.configurations = this.configurations || []
            }
            this.ctx.ready = true
          })
      },
      _persistCounterContext() {
        // Se llama solo desde handlers .then() de respuestas exitosas (ya hubo conexión).
        // Guardamos únicamente un contexto usable y nunca durante un estado offline restaurado.
        if (!this.ctx.price_list_id) return
        contextStorage.save({
          savedAt: new Date().toISOString(),
          staffId: Helper.getStaffId(),
          configurations: this.configurations || [],
          ivaConditions: this.arr.ivaConditions || [],
          financesCash: this.arr.financesCash || [],
          staff: this.finances.staff || null,
          offlineDefaultContext: this.offlineDefaultContext || this._buildCounterContextSnapshot(),
          ctx: this._buildCounterContextSnapshot(),
        })
      },
      _buildCounterContextSnapshot() {
        return {
          price_list_id: this.ctx.price_list_id,
          price_list: this.ctx.price_list,
          customers_id: this.ctx.customers_id,
          customer: this.ctx.customer,
          points_sales_id: this.ctx.points_sales_id,
          points_sales: this.ctx.points_sales,
          types_vouchers_id: this.ctx.types_vouchers_id,
          types_vouchers: this.ctx.types_vouchers,
          defaultMethodsPaymentId: this.ctx.defaultMethodsPaymentId,
          defaultMethodsPayment: this.ctx.defaultMethodsPayment,
          quickConcept: this.quickConcept,
        }
      },
      _captureOfflineDefaultContext() {
        if (!this.ctx.price_list_id && !this.ctx.customers_id && !this.ctx.points_sales_id) return
        this.offlineDefaultContext = this._buildCounterContextSnapshot()
      },
      _applyCounterContextSnapshot(context) {
        if (!context) return false
        this.ctx.price_list_id = context.price_list_id || 0
        this.ctx.price_list = context.price_list || null
        this.ctx.customers_id = context.customers_id || 0
        this.ctx.customer = context.customer || null
        this.ctx.points_sales_id = context.points_sales_id || 0
        this.ctx.points_sales = context.points_sales || null
        this.ctx.types_vouchers_id = context.types_vouchers_id || 0
        this.ctx.types_vouchers = context.types_vouchers || null
        this.ctx.defaultMethodsPaymentId = context.defaultMethodsPaymentId || 0
        this.ctx.defaultMethodsPayment = context.defaultMethodsPayment || null
        this.quickConcept = context.quickConcept || null
        this.contextSelection.pointSaleId = this.ctx.points_sales_id
        return true
      },
      _configurationValueNumber(configurations, id) {
        const config = (configurations || []).find(item => parseInt(item.id) === parseInt(id))
        return config ? parseInt(config.value_number || 0) : 0
      },
      _deriveOfflineDefaultContext(snapshot) {
        if (!snapshot) return null
        if (snapshot.offlineDefaultContext) return snapshot.offlineDefaultContext

        const current = snapshot.ctx || {}
        const staff = snapshot.staff || {}
        const point = staff.point_sale || staff.PointSale || null
        const pointId = staff.points_sales_id || (point && point.id) || 0
        const priceListId = this._configurationValueNumber(snapshot.configurations, 16)
        const customerId = this._configurationValueNumber(snapshot.configurations, 2)
        const methodId = this._configurationValueNumber(snapshot.configurations, 17)

        return {
          price_list_id: priceListId,
          price_list: parseInt(current.price_list_id || 0) === parseInt(priceListId || 0) ? current.price_list : null,
          customers_id: customerId,
          customer: parseInt(current.customers_id || 0) === parseInt(customerId || 0) ? current.customer : null,
          points_sales_id: parseInt(pointId || 0),
          points_sales: point || null,
          types_vouchers_id: current.types_vouchers_id || 0,
          types_vouchers: current.types_vouchers || null,
          defaultMethodsPaymentId: methodId,
          defaultMethodsPayment: parseInt(current.defaultMethodsPaymentId || 0) === parseInt(methodId || 0)
            ? current.defaultMethodsPayment
            : null,
          quickConcept: current.quickConcept || snapshot.quickConcept || null,
        }
      },
      _restoreCounterContext() {
        const snapshot = contextStorage.load(Helper.getStaffId())
        const context = this._deriveOfflineDefaultContext(snapshot)
        if (!context || !context.price_list_id) return false

        this.configurations = snapshot.configurations || []
        this.arr.ivaConditions = snapshot.ivaConditions || []
        this.arr.financesCash = snapshot.financesCash || []
        this.finances.staff = snapshot.staff || null
        this.offlineDefaultContext = context

        this._applyCounterContextSnapshot(this.offlineDefaultContext)
        this.offlineContextForced.active = true
        this.offlineContextForced.appliedAt = new Date().toISOString()
        this.pointSaleRestorePending = true

        // Offline no se pueden gestionar cajas: ir directo a facturar.
        this.cajaFlow.show = false
        return true
      },
      _forceOfflineDefaultContext(options = {}) {
        if (this.offlineContextForced.active) return
        if (!this.offlineDefaultContext) {
          const snapshot = contextStorage.load(Helper.getStaffId())
          if (snapshot) {
            this.offlineDefaultContext = this._deriveOfflineDefaultContext(snapshot)
            this.configurations = this.configurations && this.configurations.length
              ? this.configurations
              : (snapshot.configurations || [])
            this.arr.ivaConditions = this.arr.ivaConditions && this.arr.ivaConditions.length
              ? this.arr.ivaConditions
              : (snapshot.ivaConditions || [])
            this.arr.financesCash = this.arr.financesCash && this.arr.financesCash.length
              ? this.arr.financesCash
              : (snapshot.financesCash || [])
            this.finances.staff = this.finances.staff || snapshot.staff || null
          }
        }
        if (!this._applyCounterContextSnapshot(this.offlineDefaultContext)) return
        this.offlineContextForced.active = true
        this.offlineContextForced.appliedAt = new Date().toISOString()
        this.pointSaleRestorePending = true
        this.cajaFlow.show = false
        if (!options.silent) {
          this.showToast('Modo offline: se forzó cliente, punto de venta, lista y método de pago por defecto.', 'warning', 5000)
        }
      },
      loadConfiguredMethodPayment() {
        this.ctx.defaultMethodsPaymentId = this.salesCounterMethodPaymentDefault
        this.ctx.defaultMethodsPayment = null
        if (!this.ctx.defaultMethodsPaymentId) return Promise.resolve()
        return this.loadDefaultMethodPayment()
      },
      loadConfiguredQuickConcept() {
        const conceptId = this.salesCounterQuickConceptDefault
        this.quickConcept = null
        if (!conceptId) return Promise.resolve()

        return serviceAPI.mostrarConcepto(conceptId).then(response => {
          const concept = response.data || null
          if (!concept || !concept.id || !concept.iva_conditions_id) {
            console.warn('[salesCounter] configured quick concept is invalid:', conceptId)
            return
          }
          this.quickConcept = concept
        }).catch(error => {
          console.warn('[salesCounter] quick concept load error:', error)
        })
      },
      loadConfiguredPriceList() {
        const priceListId = this.salesCounterPriceListDefault
        this.ctx.price_list_id = priceListId
        this.ctx.price_list = null
        if (!priceListId) return Promise.resolve()

        return serviceAPI.mostrarListaPrecios(priceListId).then(response => {
          this.ctx.price_list = response.data || null
          if (!this.ctx.price_list) this.ctx.price_list_id = 0
        }).catch(() => {
          this.ctx.price_list = null
          this.ctx.price_list_id = 0
        })
      },
      loadDefaultCustomer(id) {
        return serviceAPI.mostrarCliente(id).then(async response => {
          const customer = response.data
          if (!customer) {
            this.ctx.ready = true
            return
          }

          this.ctx.customer = customer
          this.ctx.customers_id = customer.id

          this.getFinancesCash()

          this.ctx.ready = true
          this._loadProductCacheMeta()
          this._updateLayout()
          this.$nextTick(() => this.focusBarcode())

          if (this.ctx.customers_id && this.ctx.points_sales_id) {
            return this.searchVoucherByCustomer()
          }
        }).catch(() => { this.ctx.ready = true })
      },
      loadDefaultMethodPayment() {
        return serviceAPI.obtenerMetodosPagos().then(response => {
          const methods = Array.isArray(response.data) ? response.data : []
          this.ctx.defaultMethodsPayment = methods.find(method => parseInt(method.id) === parseInt(this.ctx.defaultMethodsPaymentId)) || null
        }).catch(e => {
          console.warn('[salesCounter] method payment load error:', e)
          this.ctx.defaultMethodsPayment = null
        })
      },
      searchVoucherByCustomer() {
        return serviceAPI.obtenerVoucherByCustomer({
          customers_id: this.ctx.customers_id,
          point_sale_id: this.ctx.points_sales_id,
          reference: 'factura',
        }).then(response => {
          if (response.data && response.data[0]) {
            this.ctx.types_vouchers_id = response.data[0].id
            this.ctx.types_vouchers = response.data[0]
          }
          // Tipo de comprobante resuelto: refrescar snapshot offline.
          this._persistCounterContext()
        }).catch(() => { this.ctx.ready = true })
      },
      showStaff() {
        const staffID = Helper.getStaffId()
        return serviceAPI.mostrarStaff(staffID).then(response => {
          this.finances.staff = response.data || null
          this.applyStaffPointSale()
          this.checkCajaFlow()
        }).catch(() => { this.checkCajaFlow() })
      },
      staffPointSaleId() {
        const staff = this.finances.staff || {}
        const point = staff.point_sale || staff.PointSale || null
        return parseInt(staff.points_sales_id || (point && point.id) || 0)
      },
      applyStaffPointSale() {
        const staff = this.finances.staff || {}
        const point = staff.point_sale || staff.PointSale || null
        this.ctx.points_sales_id = this.staffPointSaleId()
        this.ctx.points_sales = point || null
        this.contextSelection.pointSaleId = this.ctx.points_sales_id
      },
      // Punto de venta elegido manualmente por el operador (distinto al del staff).
      // Se mantiene entre ventas hasta que lo cambie, salga de la pantalla o recargue.
      getPointSaleOverride() {
        const chosen = this.pointSaleOverride
        if (!chosen || parseInt(chosen.id) === this.staffPointSaleId()) return null
        return this.findPointSaleById(chosen.id) || chosen
      },
      rememberPointSaleOverride(point) {
        this.pointSaleRestorePending = false
        const isDefault = !point || parseInt(point.id) === this.staffPointSaleId()
        this.pointSaleOverride = isDefault ? null : point
      },
      applyPreferredPointSale() {
        this.applyStaffPointSale()
        const point = this.getPointSaleOverride()
        if (!point) return
        this.ctx.points_sales_id = parseInt(point.id)
        this.ctx.points_sales = point
        this.contextSelection.pointSaleId = this.ctx.points_sales_id
      },
      // Reaplica el punto de venta elegido sobre un contexto que quedó en el
      // punto por defecto tras un período offline.
      restorePointSaleOverride(options = {}) {
        const point = this.getPointSaleOverride()
        if (!point || parseInt(point.id) === parseInt(this.ctx.points_sales_id || 0)) {
          this.pointSaleRestorePending = false
          return Promise.resolve()
        }
        // Con un ticket en curso no se cambia el punto de venta por debajo del
        // operador: queda pendiente y se aplica al iniciar la próxima venta.
        const busy = this.items.length || this.paymentShow || this.modal.confirm.active || this.isSaving
        if (busy) return Promise.resolve()

        this.pointSaleRestorePending = false
        this.ctx.points_sales_id = parseInt(point.id)
        this.ctx.points_sales = point
        this.contextSelection.pointSaleId = this.ctx.points_sales_id
        this.ctx.types_vouchers_id = 0
        this.ctx.types_vouchers = null
        this.getFinancesCash()
        this.checkCajaFlow()
        if (options.notify) {
          this.showToast(`Se restableció el punto de venta elegido: ${this.formatPointSaleLabel(point)}.`, 'info', 5000)
        }
        if (!this.ctx.customers_id) return Promise.resolve()
        return this.searchVoucherByCustomer()
      },
      // Actualiza la elección con los datos vigentes del punto de venta
      // (activo, conexión ARCA) y la descarta si el punto ya no existe.
      refreshPointSaleOverride() {
        const chosen = this.pointSaleOverride
        const points = this.arr.pointsSales || []
        if (!chosen || !points.length) return
        if (parseInt(chosen.id) === this.staffPointSaleId()) {
          this.pointSaleOverride = null
          return
        }
        const isCurrent = parseInt(chosen.id) === parseInt(this.ctx.points_sales_id || 0)
        const fresh = points.find(point => parseInt(point.id) === parseInt(chosen.id))
        if (fresh) {
          this.pointSaleOverride = fresh
          if (isCurrent) this.ctx.points_sales = fresh
          return
        }

        this.pointSaleOverride = null
        if (!isCurrent || this.offlineContextForced.active) return
        this.showToast('El punto de venta elegido ya no está disponible. Se volvió al punto de venta por defecto.', 'warning', 6000)
        this.resetTransactionContext()
      },
      async resetTransactionContext() {
        this.ctx.types_vouchers_id = 0
        this.ctx.types_vouchers = null

        if (!this.apiOnline) {
          // Offline: la API no es accesible, se opera con el punto de venta por
          // defecto y su tipo de comprobante, guardados durante la última
          // sesión online. La elección del operador se retoma al reconectar.
          this.applyStaffPointSale()
          if (this.getPointSaleOverride()) this.pointSaleRestorePending = true
          const snapshot = contextStorage.load(Helper.getStaffId())
          const context = this.offlineDefaultContext || this._deriveOfflineDefaultContext(snapshot)
          if (context && context.types_vouchers_id) {
            this.ctx.types_vouchers_id = context.types_vouchers_id
            this.ctx.types_vouchers = context.types_vouchers || null
          } else if (snapshot && snapshot.ctx && snapshot.ctx.types_vouchers_id) {
            this.ctx.types_vouchers_id = snapshot.ctx.types_vouchers_id
            this.ctx.types_vouchers = snapshot.ctx.types_vouchers || null
          }
          this.getFinancesCash()
          this.checkCajaFlow()
          return
        }

        // El punto de venta elegido por el operador se mantiene entre ventas.
        const restoredAfterOffline = this.pointSaleRestorePending
        this.applyPreferredPointSale()
        this.pointSaleRestorePending = false
        if (restoredAfterOffline && this.ctx.points_sales_id !== this.staffPointSaleId()) {
          this.showToast(`Se restableció el punto de venta elegido: ${this.pointSaleLabel}.`, 'info', 5000)
        }

        if (this.customersDefault) {
          await this.loadDefaultCustomer(this.customersDefault)
        } else {
          this.getFinancesCash()
          this.checkCajaFlow()
        }
      },
      loadAvailablePointSales() {
        if (!this.apiOnline) return Promise.resolve()
        return serviceAPI.obtenerPuntosVentas().then(response => {
          this.arr.pointsSales = Array.isArray(response.data) ? response.data : []
          this.refreshPointSaleOverride()
        }).catch(e => {
          console.warn('[salesCounter] points sales load error:', e)
          this.arr.pointsSales = []
        })
      },
      pointSaleIsDeclared(point) {
        if (!point) return false
        return point.nd === false || point.nd === 0 || point.nd === '0'
      },
      formatPointSaleLabel(point) {
        if (!point) return ''
        const pointNumber = String(point.point_sale || point.number || point.id || '').padStart(4, '0')
        const name = point.name || point.reference || ''
        const business = point.business ? (point.business.reference || point.business.name || '') : ''
        const prefix = business ? `${business} - ` : ''
        const status = this.pointSaleIsDeclared(point)
          ? (point.status ? 'Declarado' : 'Declarado sin conexión ARCA')
          : 'No informado'
        return `${prefix}${pointNumber}${name ? ' - ' + name : ''} (${status})`
      },
      findPointSaleById(id) {
        const all = [
          this.finances.staff && (this.finances.staff.point_sale || this.finances.staff.PointSale),
          ...(this.arr.pointsSales || []),
        ].filter(Boolean)
        return all.find(point => parseInt(point.id) === parseInt(id)) || null
      },
      async confirmContextChange(message) {
        if (!this.items.length) return true
        return await this.$bvModal.msgBoxConfirm(
          this.$createElement('div', { domProps: { innerHTML:
            `${message}<br><br>` +
            `Hay <strong>${this.items.length}</strong> producto(s) cargados. Para cambiar el contexto se va a cancelar el ticket actual.`
          }}),
          {
            title: 'Cambiar venta mostrador',
            okTitle: 'Cambiar y cancelar ticket',
            cancelTitle: 'No cambiar',
            okVariant: 'warning',
            cancelVariant: 'outline-secondary',
            centered: true,
          }
        )
      },
      openCustomerSelector() {
        this.modal.customerSelector.key += 1
        this.modal.customerSelector.active = true
      },
      async onCustomerSelected(customer) {
        if (!customer || !customer.id) return
        if (parseInt(customer.id) === parseInt(this.ctx.customers_id)) {
          this.modal.customerSelector.active = false
          return
        }
        const ok = await this.confirmContextChange(`Vas a cambiar el cliente a <strong>${customer.name}</strong>.`)
        if (!ok) return
        this.items = []
        this.selectedItem = null
        this.resetGeneralDiscount()
        this.paymentShow = false
        this.modal.customerSelector.active = false
        serviceAPI.mostrarCliente(customer.id).then(response => {
          this.ctx.customer = response.data || customer
          this.ctx.customers_id = this.ctx.customer.id
          this.searchVoucherByCustomer()
          this.focusBarcode()
        }).catch(() => {
          this.ctx.customer = customer
          this.ctx.customers_id = customer.id
          this.searchVoucherByCustomer()
          this.focusBarcode()
        })
      },
      async onPointSaleSelected(pointSaleId) {
        const nextId = parseInt(pointSaleId || 0)
        if (!nextId || nextId === parseInt(this.ctx.points_sales_id || 0)) return
        const point = this.findPointSaleById(nextId)
        if (!point) {
          this.contextSelection.pointSaleId = this.ctx.points_sales_id
          return
        }
        const ok = await this.confirmContextChange(`Vas a cambiar el punto de venta a <strong>${this.formatPointSaleLabel(point)}</strong>.`)
        if (!ok) {
          this.contextSelection.pointSaleId = this.ctx.points_sales_id
          return
        }

        this.items = []
        this.selectedItem = null
        this.resetGeneralDiscount()
        this.paymentShow = false
        this.ctx.points_sales_id = point.id
        this.ctx.points_sales = point
        this.contextSelection.pointSaleId = point.id
        this.rememberPointSaleOverride(point)
        this.ctx.types_vouchers_id = 0
        this.ctx.types_vouchers = null
        this.searchVoucherByCustomer()
        this.getFinancesCash()
        this.checkCajaFlow()
        this.focusBarcode()
      },

      // ── Barcode / Productos ───────────────────────────────────────────────────
      onBarcodeInput() {
        if (this.barcode.blocked) return
        // El input es readonly: no hay tipeo manual. Timer corto como fallback para scanners sin Enter.
        if (this.barcode._autoTimer) clearTimeout(this.barcode._autoTimer)
        if (!this.barcode.buffer.trim()) return
        this.barcode._autoTimer = setTimeout(() => {
          this.barcode._autoTimer = null
          if (!this.barcode.buffer.trim()) return
          if (this.barcode.searching) {
            const code = this.barcode.buffer.trim()
            this.barcode.buffer = ''
            if (this.$refs.barcodeInput && this.$refs.barcodeInput.$el)
              this.$refs.barcodeInput.$el.value = ''
            this.barcode.queue.push(code)
          } else {
            this.processBarcode()
          }
        }, 200)
      },
      processBarcode() {
        if (this.barcode.blocked) return
        this._initAudioCtx()
        if (this.barcode._autoTimer) {
          clearTimeout(this.barcode._autoTimer)
          this.barcode._autoTimer = null
        }
        const code = this.barcode.buffer.trim()
        // Limpiar sincrónicamente: modelo Vue + valor nativo del DOM
        // sin $nextTick para que el scanner no alcance a escribir en el input viejo
        this.barcode.buffer = ''
        if (this.$refs.barcodeInput && this.$refs.barcodeInput.$el)
          this.$refs.barcodeInput.$el.value = ''
        if (!code) return
        if (this.barcode.searching) {
          // Ya hay una búsqueda en curso: encolar para procesar después
          this.barcode.queue.push(code)
          return
        }
        this.searchAndAdd(code)
      },
      async searchAndAdd(code) {
        const weighedLabel = this._parseWeighedBarcode(code)
        // Un código normal re-escaneado suma cantidad. Las etiquetas de
        // balanza siempre agregan una línea: cada una puede traer un importe
        // distinto, aun cuando se repita el mismo formato/código.
        const existingByBarcode = this.items.find(i => i.barcode === code || i.code === code)
        if (!weighedLabel && existingByBarcode) {
          existingByBarcode.quantity = parseFloat((Number(existingByBarcode.quantity) + 1).toFixed(3))
          this.playBeepSuccess()
          this.showToast(`<b>+1</b> — ${existingByBarcode.name}`, 'success', 1500)
          if (this.barcode.queue.length) {
            const next = this.barcode.queue.shift()
            this.$nextTick(() => this.searchAndAdd(next))
          } else {
            this.focusBarcode()
          }
          return
        }

        this.barcode.searching = true
        try {
          if (weighedLabel) {
            await this._addWeighedBarcodeProduct(code, weighedLabel)
            return
          }

          // Siempre desde IndexedDB — sin fallback a la API
          const productData = await this._getCachedProduct(code)

          if (!productData) {
            this.playBeepError()
            this.showToast(`"<b>${code}</b>" no está en el catálogo local. Actualizá el catálogo.`, 'warning')
            await this.requireUnknownBarcodeAcknowledgement(code)
            return
          }

          if (!this._hasValidProductPrice(productData)) {
            this.playBeepError()
            this.showToast(`Sin precio en lista actual: ${productData.product.name}`, 'warning')
            return
          }

          const { product, price, priceNet, aliquotIva, priceList, currency } = productData

          // Mismo producto registrado con otro código
          const existingById = this.items.find(i => i.product_id === product.id)
          if (existingById) {
            existingById.quantity = parseFloat((Number(existingById.quantity) + 1).toFixed(3))
            this.playBeepSuccess()
            this.showToast(`<b>+1</b> — ${product.name}`, 'success', 1500)
            return
          }

          this.items.push({
            _uid: `${Date.now()}_${Math.random()}`,
            product: { ...product, source: 'product' },
            product_id: product.id,
            barcode: code,
            code: product.code,
            name: product.name,
            quantity: 1,
            price,
            price_net: priceNet,
            aliquot_iva: aliquotIva,
            price_list: priceList,
            currency,
            unit_measurement: product.unit_measurement_sale,
          })

          this.playBeepSuccess()
          this.showToast(`<b>Agregado</b> — ${product.name}`, 'success', 1500)
        } catch(e) {
          this.playBeepError()
          this.showToast('Error al leer el catálogo local', 'error')
          console.error('[salesCounter] searchAndAdd:', e)
        } finally {
          this.barcode.searching = false
          if (this.barcode.queue.length) {
            const next = this.barcode.queue.shift()
            this.$nextTick(() => this.searchAndAdd(next))
          } else if (!this.productSearch.active) {
            this.focusBarcode()
          }
        }
      },
      async requireUnknownBarcodeAcknowledgement(code) {
        this.barcode.blocked = true
        // No procesar automáticamente códigos que hayan llegado mientras se
        // resolvía el faltante: deben escanearse nuevamente tras reconocerlo.
        this.barcode.queue = []
        let searchManually = false
        try {
          const content = this.$createElement('div', [
            this.$createElement('div', { class: 'text-danger font-weight-bold mb-2' }, 'El producto no se agregó.'),
            this.$createElement('p', { class: 'mb-2' }, [
              'No se encontró el código ', this.$createElement('strong', String(code)), ' en el catálogo local.'
            ]),
            this.$createElement('p', { class: 'mb-0' }, 'Verificá el producto, buscálo manualmente o confirmá que vas a continuar con otro.'),
          ])
          const continueWithNext = await this.$bvModal.msgBoxConfirm(
            content,
            {
              title: 'Código de barras no encontrado',
              okTitle: 'Continuar con otro producto',
              cancelTitle: 'Buscar manualmente',
              okVariant: 'primary',
              cancelVariant: 'outline-secondary',
              autoFocusButton: 'cancel',
              centered: true,
              noCloseOnBackdrop: true,
              noCloseOnEsc: true,
              hideHeaderClose: true,
            }
          )
          searchManually = continueWithNext === false
        } finally {
          this.barcode.blocked = false
        }

        if (searchManually) this.openProductSearch()
      },
      // ── Online / Offline ──────────────────────────────────────────────────────
      _onOnline()  {
        this.isOnline = true
        this.runConnectivityCheck({ silent: true }).then(online => {
          if (!online) return
          this.offlineContextForced.active = false
          this.showToast('Conexión recuperada. Reintentando sincronizar comprobantes pendientes.', 'success', 3500)
          this.loadAvailablePointSales()
          saleQueue.retryFailed()
          saleQueue.startProcessing()
        })
      },
      _onOffline() {
        this.isOnline = false
        this.internetStatus.online = false
        this.internetStatus.error = 'Sin conexión a internet'
        this.apiStatus.online = false
        this.apiStatus.error = 'Sin conexión del navegador'
        saleQueue.setOnline(false)
        this._forceOfflineDefaultContext({ silent: true })
        this.showToast('Sin conexión. La venta seguirá en modo offline y se sincronizará al volver la conexión.', 'warning', 5000)
      },
      syncBrowserOnlineState() {
        this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
        if (!this.isOnline) {
          this.internetStatus.online = false
          this.internetStatus.error = 'Sin conexión a internet'
          this.apiStatus.online = false
          this.apiStatus.error = 'Sin conexión del navegador'
          saleQueue.setOnline(false)
        }
      },
      startConnectivityWatch() {
        this.stopConnectivityWatch()
        this.runConnectivityCheck({ silent: true })
        this._connectionTimer = window.setInterval(() => {
          this.runConnectivityCheck({ silent: true })
        }, CONNECTIVITY_CHECK_INTERVAL_MS)
      },
      stopConnectivityWatch() {
        if (this._connectionTimer) {
          window.clearInterval(this._connectionTimer)
          this._connectionTimer = null
        }
      },
      startClockTick() {
        this.stopClockTick()
        this._clockTimer = window.setInterval(() => {
          this.clockNow = Date.now()
        }, 30000)
      },
      stopClockTick() {
        if (this._clockTimer) {
          window.clearInterval(this._clockTimer)
          this._clockTimer = null
        }
      },
      async runConnectivityCheck(options = {}) {
        this.syncBrowserOnlineState()

        const apiOnline = await this.checkApiStatus(options)
        this.internetStatus.online = apiOnline
        this.internetStatus.error = apiOnline ? '' : 'Sin conexión al servidor'
        this.internetStatus.lastCheckedAt = this.apiStatus.lastCheckedAt
        if (apiOnline) {
          this.offlineContextForced.active = false
          saleQueue.setOnline(true)
          saleQueue.startProcessing()
          if (this.pointSaleRestorePending) this.restorePointSaleOverride({ notify: true })
        } else {
          saleQueue.setOnline(false)
          this._forceOfflineDefaultContext()
        }
        return apiOnline
      },
      async checkApiStatus(options = {}) {
        if (this.apiStatus.checking) return this.apiStatus.online === true
        this.apiStatus.checking = true
        try {
          await Promise.race([
            serviceAPI.obtenerConfiguracion(),
            new Promise((resolve, reject) => setTimeout(() => reject(new Error('Timeout de API')), API_CHECK_TIMEOUT_MS)),
          ])
          this.apiStatus.online = true
          this.apiStatus.error = ''
          this.apiStatus.lastCheckedAt = new Date().toISOString()
          return true
        } catch(e) {
          this.apiStatus.online = false
          this.apiStatus.error = e.message || 'API sin respuesta'
          this.apiStatus.lastCheckedAt = new Date().toISOString()
          if (!options.silent) this.showToast('La API no respondió', 'warning')
          return false
        } finally {
          this.apiStatus.checking = false
        }
      },
      async checkOfflineStorage() {
        if (this.offlineStorage.checking) return
        this.offlineStorage.checking = true
        try {
          const [queueHealth, catalogHealth] = await Promise.all([
            saleQueue.checkStorageHealth(),
            catalogStorage.checkHealth(),
          ])
          const ok = queueHealth.ok && catalogHealth.ok
          this.offlineStorage.ready = ok
          this.offlineStorage.error = ok
            ? ''
            : [queueHealth.error, catalogHealth.error].filter(Boolean).join(' | ')
        } catch(e) {
          this.offlineStorage.ready = false
          this.offlineStorage.error = e.message || 'IndexedDB no disponible'
        } finally {
          this.offlineStorage.checking = false
        }
      },

      // ── Prefetch ──────────────────────────────────────────────────────────────
      async _startPrefetch() {
        if (this.prefetch.active || this.prefetch.done) return
        // Sin conexión con el servidor: no intentar actualizar el catálogo;
        // se sigue usando el ya cacheado en IndexedDB.
        if (this.apiStatus.online === false) return
        // Esperar a que ivaConditions estén disponibles (máx 5s)
        if (!this.arr.ivaConditions.length) {
          await new Promise(resolve => {
            const unwatch = this.$watch('arr.ivaConditions', val => {
              if (val && val.length) { unwatch(); resolve() }
            })
            setTimeout(resolve, 5000)
          })
        }
        this._prefetchAllProducts()
      },

      async _prefetchAllProducts() {
        if (this.prefetch.active || !this.ctx.price_list_id) return
        this.prefetch.active = true
        const currentCount = this.offlineCatalog.productsCount || await this._countCachedProductsForCurrentList()
        this._saveProductCacheMeta({
          status: 'updating',
          productsCount: currentCount,
          updatedAt: this.offlineCatalog.updatedAt,
          priceListId: this.ctx.price_list_id,
          error: '',
        })
        this._setCatalogProgress('download', 0, 2, 'Descargando productos y precios')
        try {
          const [productsRes, pricesRes] = await Promise.all([
            cpbtDetailService.obtenerProductos({
              where: [{ field: 'm_products.active', condition: true }],
            }),
            serviceAPI.obtenerPreciosLista({ price_list_id: this.ctx.price_list_id, slim: true }),
          ])

          const products  = productsRes.data || []
          const priceMap  = {}
          const prices = pricesRes.data || []
          this._setCatalogProgress('prices', 1, 2, `Procesando ${prices.length} precio(s)`)
          prices.forEach(priceItem => {
            const productId = this._priceProductId(priceItem)
            if (productId) priceMap[productId] = priceItem
          })

          const updatedAt = new Date().toISOString()
          const listData = this.ctx.price_list || null
          const currency = listData ? listData.currency : null
          const entries = []

          products.forEach((product, index) => {
            const productId  = this._productId(product)
            const priceItem  = priceMap[productId]
            const priceNet   = this._normalizePriceNet(priceItem)
            // También se guardan los productos con precio cero, pero solo para
            // que una etiqueta de balanza configurada pueda identificarlos.
            // Siguen excluidos de la búsqueda y el escaneo normal.
            if (!priceItem || priceNet < 0) return

            const ivaCond    = (this.arr.ivaConditions || []).find(c => c.id == product.erp_iva_condition_id)
            const aliquotIva = ivaCond ? parseFloat(ivaCond.aliquot || 0) : 0
            // El snapshot final es la fuente de verdad offline: se calcula en
            // servidor antes de redondear el neto. Recalcular IVA sobre
            // priceNet (ya redondeado) puede alterar un centavo el total.
            // El fallback preserva compatibilidad con catálogos antiguos que
            // todavía no tengan amount_total_iva_snapshot.
            const priceFinalSnapshot = this._normalizePriceFinalSnapshot(priceItem)
            const price      = priceFinalSnapshot !== null
              ? priceFinalSnapshot
              : (aliquotIva > 0
                ? parseFloat((priceNet * (1 + aliquotIva / 100)).toFixed(2))
                : priceNet)

            const entry = {
              code: product.code,
              key: `${product.code}|${this.ctx.price_list_id}`,
              productId,
              priceListId: parseInt(this.ctx.price_list_id),
              product: {
                id:                   productId,
                code:                 product.code,
                name:                 product.name,
                barcode:              product.barcode,
                erp_iva_condition_id: product.erp_iva_condition_id,
                unit_measurement_sale: product.unit_measurement_sale_id
                  ? { id: product.unit_measurement_sale_id }
                  : null,
              },
              price, priceNet, aliquotIva,
              availableForNormalSale: priceNet > 0,
              priceList: listData,
              currency,
              updatedAt,
              categoryName: (product.subcategory && product.subcategory.category) ? (product.subcategory.category.name || '') : '',
              subcategoryName: product.subcategory ? (product.subcategory.name || '') : '',
            }

            entries.push(entry)
            if (product.barcode) {
              entries.push({
                ...entry,
                key: `${product.barcode}|${this.ctx.price_list_id}`,
                code: product.barcode,
              })
            }
            if (index === 0 || index === products.length - 1 || index % 100 === 0) {
              this._setCatalogProgress('products', index + 1, products.length, `Preparando ${products.length} producto(s)`)
            }
          })

          const productsCount = Object.keys(entries.reduce((acc, entry) => {
            if (entry.productId) acc[entry.productId] = true
            return acc
          }, {})).length

          if (!productsCount) {
            throw new Error(`No hay productos cacheables para la lista actual (productos: ${products.length}, precios: ${prices.length})`)
          }

          this._setCatalogProgress('storage', entries.length, entries.length, 'Guardando catálogo offline')
          const saved = await catalogStorage.replaceProducts(entries)
          if (!saved) {
            throw new Error('No se pudo guardar catálogo offline')
          }

          this._saveProductCacheMeta({
            status: productsCount ? 'ready' : 'empty',
            productsCount,
            updatedAt,
            priceListId: this.ctx.price_list_id,
            error: '',
          })
          this._setCatalogProgress('done', productsCount, productsCount, 'Catálogo actualizado')
          this.prefetch.done = true
        } catch(e) {
          this._saveProductCacheMeta({
            status: 'error',
            productsCount: await this._countCachedProductsForCurrentList(),
            updatedAt: this.offlineCatalog.updatedAt,
            priceListId: this.ctx.price_list_id,
            error: e.message || 'No se pudo actualizar',
          })
          this._setCatalogProgress('error', 0, 0, 'No se pudo actualizar catálogo')
          console.warn('[salesCounter] prefetch error:', e)
        } finally {
          this.prefetch.active = false
        }
      },

      _isCacheStale() {
        if (!this.offlineCatalog.updatedAt) return true
        if (parseInt(this.offlineCatalog.priceFormatVersion || 0) !== CATALOG_PRICE_FORMAT_VERSION) return true
        return Date.now() - new Date(this.offlineCatalog.updatedAt).getTime() > 60 * 60 * 1000
      },
      async _getCachedProduct(code) {
        const entry = await catalogStorage.getProduct(code, this.ctx.price_list_id)
        if (!entry) return null
        return {
          product: entry.product,
          price: entry.price,
          priceNet: entry.priceNet,
          aliquotIva: entry.aliquotIva,
          priceList: entry.priceList,
          currency: entry.currency,
        }
      },
      async _getCachedProductForWeighedLabel(code) {
        const entry = await catalogStorage.getProduct(code, this.ctx.price_list_id)
        if (!entry) return null
        return {
          product: entry.product,
          price: entry.price,
          priceNet: entry.priceNet,
          aliquotIva: entry.aliquotIva,
          priceList: entry.priceList,
          currency: entry.currency,
        }
      },
      _parseWeighedBarcode(code) {
        const value = String(code || '').trim()
        for (const format of this.weighedBarcodeFormats) {
          try {
            const regex = new RegExp(format.regex || '')
            const match = regex.exec(value)
            if (!match || match.index !== 0 || match[0] !== value) continue

            const productCode = match[parseInt(format.productGroup || 1)]
            const rawPrice = match[parseInt(format.priceGroup || 2)]
            const decimals = Math.max(0, Math.min(6, parseInt(format.priceDecimals || 0)))
            if (productCode === undefined || rawPrice === undefined || !/^\d+$/.test(rawPrice)) continue

            const price = Number(rawPrice) / Math.pow(10, decimals)
            if (!Number.isFinite(price) || price <= 0) continue
            return {
              productCode,
              price: this.centsToMoney(this.moneyToCents(price)),
              formatName: format.name || 'etiqueta de balanza',
            }
          } catch(e) {
            // Una regla inválida no debe interferir con los códigos normales.
          }
        }
        return null
      },
      async _addWeighedBarcodeProduct(barcode, weighedLabel) {
        const productData = await this._getCachedProductForWeighedLabel(weighedLabel.productCode)
        if (!productData) {
          this.playBeepError()
          this.showToast(`La etiqueta no corresponde a un producto del catálogo: <b>${weighedLabel.productCode}</b>`, 'warning')
          await this.requireUnknownBarcodeAcknowledgement(barcode)
          return
        }

        const { product, aliquotIva, priceList, currency } = productData
        const priceNet = aliquotIva > 0
          ? this.centsToMoney(this.moneyToCents(weighedLabel.price / (1 + aliquotIva / 100)))
          : weighedLabel.price

        this.items.push({
          _uid: `${Date.now()}_${Math.random()}`,
          // Para facturación es un producto estándar: la diferencia está en
          // el precio leído de la etiqueta, no en el tipo de detalle.
          product: { ...product, source: 'product' },
          product_id: product.id,
          barcode,
          code: product.code,
          name: product.name,
          quantity: 1,
          price: weighedLabel.price,
          price_net: priceNet,
          aliquot_iva: aliquotIva,
          price_list: priceList,
          currency,
          unit_measurement: product.unit_measurement_sale,
        })
        this.playBeepSuccess()
        this.showToast(`<b>Agregado desde ${weighedLabel.formatName}</b> — ${product.name}`, 'success', 1800)
      },
      _hasValidProductPrice(productData) {
        if (!productData) return false
        return parseFloat(productData.priceNet || 0) > 0 || parseFloat(productData.price || 0) > 0
      },
      _normalizePriceNet(priceData) {
        if (!priceData) return 0
        if (priceData.price_simple !== undefined) return parseFloat(priceData.price_simple) || 0
        if (priceData.amount_total !== undefined && parseFloat(priceData.amount_total) > 0) return parseFloat(priceData.amount_total) || 0
        if (priceData.amount_initial !== undefined) return parseFloat(priceData.amount_initial) || 0
        if (priceData.amount && priceData.amount.amount_total !== undefined && parseFloat(priceData.amount.amount_total) > 0) {
          return parseFloat(priceData.amount.amount_total) || 0
        }
        if (priceData.amount && priceData.amount.amount_initial !== undefined) return parseFloat(priceData.amount.amount_initial) || 0
        return 0
      },
      _normalizePriceFinalSnapshot(priceData) {
        if (!priceData || priceData.amount_total_iva_snapshot === undefined) return null
        const value = parseFloat(priceData.amount_total_iva_snapshot)
        return Number.isFinite(value) && value > 0 ? value : null
      },
      _priceProductId(priceData) {
        if (!priceData) return 0
        return parseInt(priceData.products_id || priceData.product_id || (priceData.product && priceData.product.id) || 0)
      },
      _productId(product) {
        if (!product) return 0
        return parseInt(product.id || product.products_id || 0)
      },
      async _loadProductCacheMeta() {
        try {
          const meta = await catalogStorage.loadMeta()
          if (meta && parseInt(meta.priceListId) === parseInt(this.ctx.price_list_id)) {
            this.offlineCatalog = {
              status: meta.status || (meta.productsCount ? 'ready' : 'empty'),
              productsCount: parseInt(meta.productsCount || 0),
              updatedAt: meta.updatedAt || null,
              priceListId: meta.priceListId || this.ctx.price_list_id,
              priceFormatVersion: parseInt(meta.priceFormatVersion || 1),
              error: meta.error || '',
              progress: this._emptyCatalogProgress(),
            }
            return
          }
        } catch(e) {
          console.warn('[salesCounter] catalog meta read error:', e)
        }

        const count = await this._countCachedProductsForCurrentList()
        this.offlineCatalog = {
          status: count ? 'ready' : 'empty',
          productsCount: count,
          updatedAt: null,
          priceListId: this.ctx.price_list_id,
          priceFormatVersion: 0,
          error: '',
          progress: this._emptyCatalogProgress(),
        }
      },
      _saveProductCacheMeta(meta) {
        const next = {
          status: meta.status,
          productsCount: parseInt(meta.productsCount || 0),
          updatedAt: meta.updatedAt || null,
          priceListId: meta.priceListId || this.ctx.price_list_id,
          priceFormatVersion: CATALOG_PRICE_FORMAT_VERSION,
          error: meta.error || '',
          progress: meta.progress || this.offlineCatalog.progress || this._emptyCatalogProgress(),
        }
        this.offlineCatalog = next
        catalogStorage.saveMeta(next)
      },
      _emptyCatalogProgress() {
        return { step: '', current: 0, total: 0, percent: 0, message: '' }
      },
      _setCatalogProgress(step, current, total, message) {
        const safeTotal = parseInt(total || 0)
        const safeCurrent = parseInt(current || 0)
        const percent = safeTotal > 0 ? Math.min(100, Math.round((safeCurrent / safeTotal) * 100)) : 0
        this.offlineCatalog = {
          ...this.offlineCatalog,
          progress: { step, current: safeCurrent, total: safeTotal, percent, message },
        }
      },
      async _countCachedProductsForCurrentList() {
        return await catalogStorage.countProducts(this.ctx.price_list_id)
      },
      focusBarcode() {
        setTimeout(() => {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus()
        }, 80)
      },
      onQtyChange(item) {
        if (!item.quantity || Number(item.quantity) <= 0) item.quantity = 1
      },
      // ── Búsqueda por nombre / categoría ──────────────────────────────────────
      openProductSearch() {
        this.productSearch.active = true
        this.$nextTick(() => {
          if (this.$refs.productSearchInput) this.$refs.productSearchInput.focus()
        })
      },
      closeProductSearch() {
        if (this.productSearch._timer) clearTimeout(this.productSearch._timer)
        this.productSearch.active = false
        this.productSearch.query = ''
        this.productSearch.results = []
        this.productSearch.show = false
        this.productSearch.activeIdx = -1
        this.productSearch.loading = false
        this.focusBarcode()
      },
      onProductSearchInput() {
        // Solo limpia resultados previos al escribir; la búsqueda se ejecuta con Enter
        this.productSearch.results = []
        this.productSearch.show = false
        this.productSearch.activeIdx = -1
        this.productSearch.loading = false
      },
      async _runProductSearch() {
        const q = this.productSearch.query.trim()
        if (!q) return
        try {
          const results = await catalogStorage.searchProducts(this.ctx.price_list_id, q)
          this.productSearch.results = results
          this.productSearch.activeIdx = results.length ? 0 : -1
        } catch(e) {
          this.productSearch.results = []
        } finally {
          this.productSearch.loading = false
        }
      },
      productSearchMoveDown() {
        if (!this.productSearch.show || !this.productSearch.results.length) return
        this.productSearch.activeIdx = Math.min(this.productSearch.activeIdx + 1, this.productSearch.results.length - 1)
        this._scrollSearchItem()
      },
      productSearchMoveUp() {
        if (!this.productSearch.show) return
        this.productSearch.activeIdx = Math.max(this.productSearch.activeIdx - 1, -1)
        this._scrollSearchItem()
      },
      _scrollSearchItem() {
        this.$nextTick(() => {
          const el = this.$el.querySelector('.sc-search-item-active')
          if (el) el.scrollIntoView({ block: 'nearest' })
        })
      },
      async productSearchConfirm() {
        const q = this.productSearch.query.trim()
        if (q.startsWith('**')) {
          this.openQuickConceptFromShortcut(q)
          return
        }
        if (!q || q.replace(/\s+/g, '').length < 3) return

        // Si hay resultados mostrados: seleccionar el activo o el único
        if (this.productSearch.show && this.productSearch.results.length) {
          const idx = this.productSearch.activeIdx
          if (idx >= 0 && idx < this.productSearch.results.length) {
            this.addFromSearch(this.productSearch.results[idx])
          } else if (this.productSearch.results.length === 1) {
            this.addFromSearch(this.productSearch.results[0])
          }
          return
        }

        // Sin resultados previos: ejecutar búsqueda ahora
        await this._runProductSearch()
        this.productSearch.show = true
      },
      productSearchBlur() {
        setTimeout(() => { this.productSearch.show = false }, 150)
      },
      productSearchFocus() {
        if (this.productSearch.query.trim().replace(/\s+/g, '').length >= 3) {
          this.productSearch.show = true
        }
      },
      addFromSearch(item) {
        this._initAudioCtx()
        const existing = this.items.find(i => i.product_id === item.productId)
        if (existing) {
          existing.quantity = parseFloat((Number(existing.quantity) + 1).toFixed(3))
          this.playBeepSuccess()
          this.showToast(`<b>+1</b> — ${existing.name}`, 'success', 1500)
        } else {
          this.items.push({
            _uid: `${Date.now()}_${Math.random()}`,
            product: { ...item.product, source: 'product' },
            product_id: item.productId,
            barcode: item.product.barcode || item.code,
            code: item.product.code || item.code,
            name: item.product.name,
            quantity: 1,
            price: item.price,
            price_net: item.priceNet,
            aliquot_iva: item.aliquotIva,
            price_list: item.priceList,
            currency: item.currency,
            unit_measurement: item.product.unit_measurement_sale,
          })
          this.playBeepSuccess()
          this.showToast(`<b>Agregado</b> — ${item.product.name}`, 'success', 1500)
        }
        this.closeProductSearch()
      },
      openQuickConceptFromShortcut(query) {
        const rawPrice = String(query || '').slice(2).trim()
        const price = this.centsToMoney(this.moneyToCents(this.parseMoneyInput(rawPrice)))
        if (!rawPrice || price <= 0) {
          this.showToast('Ingresá un importe válido: **123,45', 'warning', 3500)
          return
        }
        if (!this.quickConcept) {
          this.showToast('Configurá el concepto rápido en Configuraciones de Ventas para poder usar este atajo.', 'warning', 5000)
          return
        }

        this.productSearch.active = false
        this.productSearch.query = ''
        this.productSearch.results = []
        this.productSearch.show = false
        this.productSearch.activeIdx = -1
        this.productSearch.loading = false
        this.modal.quickConcept.description = this.quickConcept.name || ''
        this.modal.quickConcept.priceInput = price
        this.modal.quickConcept.active = true
      },
      focusQuickConceptDescription() {
        this.$nextTick(() => {
          this.focusQuickConceptInput('quickConceptDescription')
        })
      },
      focusQuickConceptPrice() {
        this.$nextTick(() => {
          this.focusQuickConceptInput('quickConceptPrice')
        })
      },
      focusQuickConceptInput(refName) {
        const input = this.$refs[refName]
        if (!input) return
        if (typeof input.focus === 'function') {
          input.focus()
        } else if (input.$el && typeof input.$el.focus === 'function') {
          input.$el.focus()
        }
      },
      closeQuickConcept() {
        this.modal.quickConcept.active = false
        this.modal.quickConcept.description = ''
        this.modal.quickConcept.priceInput = 0
        this.focusBarcode()
      },
      getConceptIvaRate(concept) {
        const ivaConditionId = concept && concept.iva_conditions_id
        const ivaCondition = (this.arr.ivaConditions || []).find(item => parseInt(item.id) === parseInt(ivaConditionId))
        return ivaCondition ? parseFloat(ivaCondition.aliquot || 0) || 0 : null
      },
      getConceptFinalPrice(priceNet, concept) {
        const rate = this.getConceptIvaRate(concept)
        const net = this.centsToMoney(this.moneyToCents(priceNet))
        return this.centsToMoney(this.moneyToCents(net * (1 + (rate || 0) / 100)))
      },
      saveQuickConcept() {
        const concept = this.quickConcept
        const description = String(this.modal.quickConcept.description || '').trim()
        const price = this.centsToMoney(this.moneyToCents(this.modal.quickConcept.priceInput))
        const ivaRate = this.getConceptIvaRate(concept)

        if (!concept || !concept.id) {
          this.showToast('No hay un concepto rápido configurado.', 'warning', 3500)
          return
        }
        if (ivaRate === null) {
          this.showToast('No se pudo cargar la condición de IVA del concepto.', 'warning', 3500)
          return
        }
        if (!description) {
          this.showToast('Ingresá una descripción para el concepto.', 'warning', 3000)
          this.focusQuickConceptDescription()
          return
        }
        if (price <= 0) {
          this.showToast('Ingresá un precio mayor a cero.', 'warning', 3000)
          this.focusQuickConceptPrice()
          return
        }

        const priceNet = this.centsToMoney(this.moneyToCents(price / (1 + ivaRate / 100)))
        this.items.push({
          _uid: `${Date.now()}_${Math.random()}`,
          item_type: 'concept',
          concept: { ...concept },
          product: null,
          product_id: 0,
          barcode: '',
          code: '',
          name: description,
          quantity: 1,
          price,
          price_net: priceNet,
          aliquot_iva: ivaRate,
          price_list: null,
          currency: null,
          unit_measurement: null,
        })
        this.playBeepSuccess()
        this.showToast(`<b>Agregado</b> — ${description}`, 'success', 1500)
        this.closeQuickConcept()
      },

      deleteItem(idx) {
        if (this.selectedItem === this.items[idx]) this.selectedItem = null
        this.items.splice(idx, 1)
        if (!this.items.length) this.resetGeneralDiscount()
      },
      confirmDeleteItem(idx, item) {
        const lines = [
          `<strong>${item.name}</strong>`,
          `Cantidad: <strong>${item.quantity}</strong> · Subtotal: <strong>${this.formatCurrency(item.quantity * item.price)}</strong>`,
          '<br>Este producto será eliminado del pedido actual.',
        ].join('<br>')
        this.$bvModal.msgBoxConfirm(this.$createElement('div', { domProps: { innerHTML: lines } }), {
          title: 'Eliminar producto',
          okVariant: 'danger',
          okTitle: 'Sí, eliminar',
          cancelTitle: 'Cancelar',
          size: 'md',
          centered: true,
          headerClass: 'bg-danger text-white',
        }).then(ok => {
          if (ok) this.deleteItem(idx)
        })
      },
      onDeleteCodeInput() {
        if (this.deleteByCode._autoTimer) clearTimeout(this.deleteByCode._autoTimer)
        if (!this.deleteByCode.buffer.trim()) return
        this.deleteByCode._autoTimer = setTimeout(() => {
          this.deleteByCode._autoTimer = null
          if (this.deleteByCode.buffer.trim()) this.confirmDeleteByCode()
        }, 150)
      },
      confirmDeleteByCodeOpen() {
        this.deleteByCode.buffer  = ''
        this.deleteByCode.notFound = false
        this.deleteByCode.show    = true
      },
      confirmDeleteByCode() {
        if (this.deleteByCode._autoTimer) {
          clearTimeout(this.deleteByCode._autoTimer)
          this.deleteByCode._autoTimer = null
        }
        this._initAudioCtx()
        const code = this.deleteByCode.buffer.trim()
        // Limpiar sincrónicamente igual que el input principal
        this.deleteByCode.buffer = ''
        if (this.$refs.deleteCodeInput && this.$refs.deleteCodeInput.$el)
          this.$refs.deleteCodeInput.$el.value = ''
        if (!code) return
        const idx = this.items.findIndex(
          i => i.barcode === code || i.code === code || (i.product && i.product.code === code)
        )
        if (idx === -1) {
          this.playBeepError()
          this.deleteByCode.notFound = true
          this.$nextTick(() => {
            if (this.$refs.deleteCodeInput) this.$refs.deleteCodeInput.$el.focus()
          })
          return
        }
        const item = this.items[idx]
        const newQty = Number(item.quantity) - 1
        if (newQty <= 0) {
          this.deleteItem(idx)
          this.playBeepSuccess()
          this.deleteByCode.show = false
          this.showToast('Producto eliminado', 'success', 1500)
        } else {
          item.quantity = newQty
          this.playBeepSuccess()
          this.deleteByCode.show = false
          this.showToast(`${item.name} → <b>${newQty}</b>`, 'success', 1500)
        }
        this.focusBarcode()
      },
      resetItems() {
        const lines = [
          `Hay <strong>${this.itemsCount} ítem(s)</strong> por un total de <strong>${this.formatCurrency(this.total)}</strong>.`,
          'Si cancelás, se borrarán todos los productos del pedido y no se registrará ninguna venta.',
        ].join('<br><br>')
        this.$bvModal.msgBoxConfirm(this.$createElement('div', { domProps: { innerHTML: lines } }), {
          title: '¿Cancelar el pedido?',
          okVariant: 'danger',
          okTitle: 'Sí, cancelar pedido',
          cancelTitle: 'No, volver',
          size: 'md',
          centered: true,
          headerClass: 'bg-danger text-white',
        }).then(ok => {
          if (ok) {
            this.items = []
            this.selectedItem = null
            this.resetGeneralDiscount()
            this.resetTransactionContext()
            this.focusBarcode()
          }
        })
      },
      rowClass(item) {
        if (item === this.selectedItem) return 'sc-row-selected'
        return ''
      },
      moneyToCents(amount) {
        const value = Number(amount || 0)
        return Math.round((value + Number.EPSILON) * 100)
      },
      centsToMoney(cents) {
        return parseFloat((Number(cents || 0) / 100).toFixed(2))
      },
      formatMoneyInput(amount) {
        return Intl.NumberFormat('es-AR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(amount || 0))
      },
      parseMoneyInput(value) {
        const raw = String(value === null || value === undefined ? '' : value).trim().replace(/\s/g, '')
        if (!raw) return 0

        let normalized = raw
        if (raw.includes(',')) {
          normalized = raw.replace(/\./g, '').replace(',', '.')
        } else {
          const dotParts = raw.split('.')
          normalized = dotParts.length === 2 && dotParts[1].length <= 2
            ? raw
            : raw.replace(/\./g, '')
        }

        const parsed = Number(normalized)
        return Number.isFinite(parsed) ? parsed : 0
      },
      clampGeneralDiscount(value) {
        const percentage = Math.min(100, Math.max(0, Number(value) || 0))
        return this.centsToMoney(this.moneyToCents(percentage))
      },
      formatDiscountPercentage(value) {
        return this.clampGeneralDiscount(value).toFixed(2).replace(/\.?0+$/, '') || '0'
      },
      onGeneralDiscountInput(value) {
        this.generalDiscount.percentageInput = value
        this.generalDiscount.percentage = this.clampGeneralDiscount(this.parseMoneyInput(value))
      },
      onGeneralDiscountFocus(event) {
        this.$nextTick(() => {
          if (event && event.target) event.target.select()
        })
      },
      formatGeneralDiscountInput() {
        this.generalDiscount.percentage = this.clampGeneralDiscount(this.generalDiscount.percentage)
        this.generalDiscount.percentageInput = this.formatDiscountPercentage(this.generalDiscount.percentage)
      },
      resetGeneralDiscount() {
        this.generalDiscount.percentage = 0
        this.generalDiscount.percentageInput = '0'
      },
      setPaymentLineAmount(idx, amount) {
        const line = this.paymentLines[idx]
        if (!line) return

        line.amount = this.centsToMoney(this.moneyToCents(amount))
        this.$set(line, 'amountDisplay', this.formatMoneyInput(line.amount))
      },
      onPaymentAmountInput(idx, value) {
        const line = this.paymentLines[idx]
        if (!line) return

        this.$set(line, 'amountDisplay', value)
        line.amount = this.centsToMoney(this.moneyToCents(this.parseMoneyInput(value)))
      },
      onPaymentAmountFocus(event) {
        this.$nextTick(() => {
          if (event && event.target) event.target.select()
        })
      },
      formatPaymentAmountInput(idx) {
        const line = this.paymentLines[idx]
        if (!line) return

        this.$set(line, 'amountDisplay', this.formatMoneyInput(line.amount))
      },
      formatCurrency(amount) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount || 0)
      },
      formatDateTimeHM(dateTime) {
        if (!dateTime) return ''
        return moment(dateTime).format('HH:mm')
      },
      async refreshCache() {
        const updatedLabel = this.catalogUpdatedLabel
          ? `Última actualización: <strong>${this.catalogUpdatedLabel}</strong><br>`
          : 'El catálogo <strong>nunca fue actualizado</strong>.<br>'
        const countLabel = this.offlineCatalog.productsCount
          ? `Productos en caché: <strong>${this.offlineCatalog.productsCount}</strong><br>`
          : ''
        const ok = await this.$bvModal.msgBoxConfirm(
          this.$createElement('div', { domProps: { innerHTML:
            `${updatedLabel}${countLabel}<br>` +
            `Se va a descargar el catálogo desde el servidor y reemplazar el catálogo local si llegan datos válidos.<br>` +
            `<strong>Esto puede tardar unos segundos.</strong> ¿Continuar?`
          }}),
          {
            title: 'Actualizar catálogo offline',
            okTitle: 'Actualizar ahora',
            cancelTitle: 'Cancelar',
            okVariant: 'primary',
            cancelVariant: 'outline-secondary',
            centered: true,
          }
        )
        if (!ok) return

        clearDetailCache()
        this.offlineCatalog = {
          ...this.offlineCatalog,
          status: 'updating',
          priceListId: this.ctx.price_list_id,
          error: '',
          progress: this._emptyCatalogProgress(),
        }
        this.prefetch.done = false
        this.showToast('Actualizando catálogo...', 'info')
        this._startPrefetch()
      },

      // ── Pago ─────────────────────────────────────────────────────────────────
      openPayment() {
        if (this.ctxErrors.length) {
          this.showToast(this.ctxErrors[0].detail || this.ctxErrors[0].title, 'warning', 3500)
          return
        }
        const userCashId = this.finances.staff && this.finances.staff.cash_id
        const pointCashId = (this.ctx.points_sales || {}).cash_id
        this.paymentLines = this.arr.financesCash.map(c => {
          const isUserCash = userCashId && c.id === userCashId
          const isPointCash = !userCashId && pointCashId && c.id === pointCashId
          return {
            caja: c,
            selected: false,
            amount: 0,
            amountDisplay: this.formatMoneyInput(0),
            preferred: !!(isUserCash || isPointCash),
            preferredType: isUserCash ? 'user' : (isPointCash ? 'point' : null),
          }
        })

        this.paymentEntrega = ''

        // Restaurar selección de medios de pago de una factura recuperada
        if (this._pendingPaymentRestore && this._pendingPaymentRestore.length) {
          this._pendingPaymentRestore.forEach(saved => {
            const idx = this.paymentLines.findIndex(l => l.caja && l.caja.id === saved.cash_id)
            if (idx >= 0) {
              this.paymentLines[idx].selected = true
              this.setPaymentLineAmount(idx, saved.total || 0)
            }
          })
          this._pendingPaymentRestore = null
        } else if (this.paymentLines.length === 1) {
          // Auto-seleccionar si hay un único método
          this.toggleLine(0)
        }

        this.paymentShow = true
      },
      closePayment() {
        this.paymentShow = false
        this.focusBarcode()
      },
      openVuelto() {
        this.paymentEntrega = ''
        this.modal.vuelto.active = true
      },
      paymentBack() {
        const anySelected = this.paymentLines.some(l => l.selected)
        if (anySelected) {
          this.paymentLines.forEach((l, idx) => {
            l.selected = false
            this.setPaymentLineAmount(idx, 0)
          })
          this.paymentEntrega = ''
        } else {
          this.closePayment()
        }
      },
      toggleLine(idx) {
        const line = this.paymentLines[idx]

        if (line.selected) {
          line.selected = false
          this.setPaymentLineAmount(idx, 0)
          const remaining = this.paymentLines.filter(l => l.selected)
          if (remaining.length === 1) {
            const alreadyOthers = this.paymentLines
              .filter(l => l.selected && l !== remaining[0])
              .reduce((s, l) => s + Number(l.amount || 0), 0)
            const remainingIdx = this.paymentLines.findIndex(l => l === remaining[0])
            this.setPaymentLineAmount(remainingIdx, Math.max(0, this.total - alreadyOthers))
          }
        } else {
          line.selected = true
          const alreadyAssigned = this.paymentLines
            .filter(l => l.selected && l !== line)
            .reduce((s, l) => s + Number(l.amount || 0), 0)
          const othersSelected = this.paymentLines.filter(l => l.selected && l !== line)
          const amount = othersSelected.length === 0
            ? parseFloat(this.total.toFixed(2))
            : parseFloat(Math.max(0, this.total - alreadyAssigned).toFixed(2))
          this.setPaymentLineAmount(idx, amount)
        }
      },
      buildPaymentObject() {
        const lines = this.paymentLines.filter(l => l.selected)
        const detail = lines.map(l => ({
          cash: l.caja,
          cash_id: l.caja ? l.caja.id : null,
          total: this.centsToMoney(this.moneyToCents(l.amount)),
        }))
        const cashTotal = this.centsToMoney(detail.reduce((s, d) => s + this.moneyToCents(d.total), 0))
        return {
          total: cashTotal,
          cash: { total: cashTotal, detail },
          cheques: { total: 0, detail: [] },
        }
      },
      async requestLocalTicket(form) {
        // El ticket local es exclusivamente para ventas ND. Los puntos
        // declarados requieren conexión con ARCA y se imprimen al confirmar
        // el comprobante electrónico mediante sendPrinterSale().
        if (!this.isNdPointSale) return

        // Capturar los datos antes de cualquier await: al encolar la venta el
        // componente limpia items y totales inmediatamente.
        const data = this.buildLocalTicketData(form)

        // Reportes es la fuente de verdad del formato. Antes de cada ticket ND
        // con conexión se renueva IndexedDB; fuera de línea se conserva la
        // última plantilla descargada para no bloquear la venta local.
        if (this.isOnline) {
          await localTicketCache.warmCache(this.localPrint.reference)
        }

        localTicketCache.resolveLocalTicket(this.localPrint.reference, data)
          .then(result => { this.printLocalTicket(result) })
          .catch(err => { this.showToast(err.message || 'No se pudo generar el ticket local', 'warning') })
      },
      buildLocalTicketData(form) {
        const paymentDetails = form.payment && form.payment.cash ? (form.payment.cash.detail || []) : []
        const methodName = paymentDetails.length > 0 && paymentDetails[0].cash && paymentDetails[0].cash.name
          ? paymentDetails[0].cash.name
          : 'Efectivo'
        const products = (form.detailProducts || []).concat(form.detailProductsCompound || [], form.detailServices || [], form.detailConcepts || [])
        const details = products.map(item => {
          const product = item.product || item.products || {}
          const quantity = Number(item.quantity || 0)
          const price = Number(item.price_final !== undefined ? item.price_final : (item.price !== undefined ? item.price : item.amount || 0))
          return {
            description: item.description || item.name || product.name || 'Producto',
            unit_measurement_reference: item.unit_measurement_reference || (item.unit_measurement && item.unit_measurement.reference) || '',
            quantity,
            price_surcharge: price,
            amount_total: quantity * price,
            price_unit_iva_include: price,
          }
        })
        const subtotal = details.reduce((sum, item) => sum + item.amount_total, 0)
        return {
          letter: 'X',
          number_full: 'LOCAL',
          date: new Date().toISOString(),
          amount_total: form.payment && form.payment.total !== undefined ? form.payment.total : this.totalWithSurcharge,
          amount_net: subtotal || this.subtotal,
          amount_iva: 0,
          amount_discount: this.generalDiscountAmount,
          amount_subtotal_iva_include: subtotal || this.subtotal,
          amount_discount_iva_include: this.generalDiscountAmount,
          percentage_discount: this.generalDiscountPercentage,
          customer: {
            data_fiscal: {
              business_name: form.customer_name || 'Consumidor Final',
              documents_number: (this.ctx.customer && this.ctx.customer.data_fiscal)
                ? (this.ctx.customer.data_fiscal.documents_number || '')
                : (form.customer_document_number || ''),
            },
          },
          staff: { name: this.finances.staff ? this.finances.staff.name : '' },
          method_payment: { name: methodName },
          fiscal_condition_customer: { name: 'Consumidor Final' },
          type_voucher: { code_afip: '0', name: 'Ticket Interno' },
          details,
        }
      },
      printLocalTicket(result) {
        this.localPrint.content = ''
        this.localPrint.payload = null
        this.$nextTick(() => {
          this.localPrint.key = Date.now()

          if (result.mode === 'external') {
            this.localPrint.payload = result.payload
          } else {
            this.localPrint.content = result.content
          }
        })
      },
      onLocalTicketPrinted() {
        this.localPrint.content = ''
        this.localPrint.payload = null
      },
      onLocalTicketError() {
        this.showToast('No se pudo imprimir. La venta se guardó correctamente.', 'warning', 3000)
      },
      confirmPayment() {
        if (this.ctxErrors.length) {
          this.showToast(this.ctxErrors[0].detail || this.ctxErrors[0].title, 'warning', 3500)
          return
        }
        if (!this.canConfirm || this.isSaving) return
        this.modal.confirm.observations = ''
        this.modal.confirm.active = true
      },

      async doConfirmPayment(printTicket = false) {
        if (this.ctxErrors.length) {
          this.showToast(this.ctxErrors[0].detail || this.ctxErrors[0].title, 'warning', 3500)
          return
        }
        if (!this.canConfirm || this.isSaving) return
        this.isSaving = true

        if (!this.ctx.types_vouchers_id) {
          this.isSaving = false
          this.showToast('No se pudo determinar el tipo de comprobante. Recargá el mostrador con conexión para resolver el contexto.', 'warning', 5000)
          return
        }

        if (this.isDeclaredPointSale) {
          const apiAvailable = await this.checkApiStatus({ silent: true })
          if (!apiAvailable) {
            this.isSaving = false
            this.showToast('El punto de venta declarado requiere conexión con la API para facturar.', 'warning', 4000)
            return
          }
        }

        const form = {
          customers_id: this.ctx.customers_id,
          customer_name: this.ctx.customer ? this.ctx.customer.name : '',
          customer_document_number: this.ctx.customer ? (this.ctx.customer.document_number || '') : '',
          types_vouchers_id: this.ctx.types_vouchers_id,
          points_sales_id: this.ctx.points_sales_id,
          points_sales: this.ctx.points_sales,
          price_list_id: this.ctx.price_list_id,
          price_list: this.ctx.price_list,
          methods_payment_id: this.ctx.defaultMethodsPaymentId,
          methods_payment: null,
          staff_id: Helper.getStaffId(),
          sellers_id: 0,
          date: moment().format('YYYY-MM-DD'),
          date_expiration: moment().format('YYYY-MM-DD'),
          detailProducts: this.items.filter(item => item.item_type !== 'concept').map(item => ({
            product: item.product,
            quantity: item.quantity,
            amount: item.price_net !== undefined ? item.price_net : item.price,
            amount_raw_internal: item.price_net !== undefined ? item.price_net : item.price,
            price_final: item.price,
            aliquot_iva: item.aliquot_iva || null,
            currency: item.currency,
            unit_measurement: item.unit_measurement,
            unit_measurement_id: item.unit_measurement ? item.unit_measurement.id : 0,
            price_list: item.price_list,
            discount: 0,
            percentage_discount: 0,
          })),
          detailProductsCompound: [],
          detailServices: [],
          detailConcepts: this.items.filter(item => item.item_type === 'concept').map(item => ({
            concepts: item.concept,
            quantity: item.quantity,
            price: item.price_net,
            price_final: item.price,
            description: item.name,
            discount: 0,
          })),
          invoiceRelated: [],
          payment: this.buildPaymentObject(),
          showPayment: true,
          generateRemits: true,
          from_sales_counter: true,
          print_ticket: Boolean(printTicket),
          skip_server_print: !printTicket,
          from_electronic_sale: this.isDeclaredPointSale,
          selectTypeVoucher: 'factura',
          selectDestination: 'default',
          not_iva: false,
          observations: '',
          observations_internal: this.modal.confirm.observations || '',
          percentage_discount: this.generalDiscountPercentage,
          shipping_id: 0,
          _idempotency_key: crypto.randomUUID(),
        }

        if (printTicket && this.isNdPointSale) {
          this.requestLocalTicket(form)
        }
        saleQueue.enqueue(form)

        this.items = []
        this.selectedItem = null
        this.resetGeneralDiscount()
        this.paymentShow = false
        this.modal.confirm.active = false
        this.isSaving = false
        // El mensaje corresponde al punto de venta con el que se facturó: se
        // evalúa antes de reiniciar el contexto de la transacción.
        const wasDeclaredPointSale = this.isDeclaredPointSale
        this.resetTransactionContext()

        const toastMsg = wasDeclaredPointSale
          ? (printTicket
              ? 'Venta enviada a ARCA — se imprimirá al confirmar el comprobante electrónico'
              : 'Venta enviada a ARCA — sin impresión solicitada')
          : (printTicket
              ? 'Venta registrada localmente — impresión solicitada y sincronización en segundo plano'
              : 'Venta registrada localmente — sin impresión solicitada')
        this.showToast(toastMsg, 'success')
        this.focusBarcode()
      },

      // ── Impresión ─────────────────────────────────────────────────────────────
      sendPrinterSale(item) {
        this.crud.print.key = Math.random()
        this.crud.print.id = item.id
        this.crud.print.reference = item.reportDefault
        this.crud.print.mode = item.print_mode || 'pdf'
      },

      // ── Cola ─────────────────────────────────────────────────────────────────
      showQueueErrors() {
        if (this.ctxErrors.length) {
          this.showToast('Resolvé los errores de configuración antes de gestionar la cola', 'warning', 3500)
          return
        }
        this.modal.queueErrors.items = saleQueue.getOpenItems()
        this.modal.queueErrors.recentDone = saleQueue.getRecentDoneItems()
        this.modal.queueErrors.active = true
      },
      retryQueue() {
        if (!this.apiOnline) {
          this.showToast('Sin conexión. Los comprobantes se enviarán cuando vuelva la conexión.', 'warning', 3500)
          return
        }
        saleQueue.retryFailed()
        this.modal.queueErrors.active = false
      },
      async syncQueueNow() {
        const online = await this.runConnectivityCheck({ silent: true })
        if (!online) {
          this.showToast('Sin conexión. Los comprobantes se enviarán cuando vuelva la conexión.', 'warning', 3500)
          return
        }
        saleQueue.retryFailed()
        saleQueue.startProcessing()
        this.modal.queueErrors.items = saleQueue.getOpenItems()
        this.modal.queueErrors.recentDone = saleQueue.getRecentDoneItems()
      },
      recoverQueueStuck() {
        const recovered = saleQueue.recoverStaleProcessing()
        if (recovered) {
          this.showToast(`${recovered} comprobante(s) recuperado(s) para reintento`, 'info')
        } else {
          this.showToast('No hay comprobantes trabados para recuperar', 'info')
        }
        this.modal.queueErrors.items = saleQueue.getOpenItems()
        this.modal.queueErrors.recentDone = saleQueue.getRecentDoneItems()
      },
      toggleQueueItemDetail(id) {
        this.modal.queueErrors.expandedId = this.modal.queueErrors.expandedId === id ? null : id
      },
      copyQueueItemData(item) {
        const sd = item.saleData || {}
        const payload = {
          id: item.id,
          status: item.status,
          enqueuedAt: item.enqueuedAt,
          customerName: item.customerName,
          customerDocument: item.customerDocument,
          pointSale: item.pointSale,
          idempotencyKey: item.idempotencyKey,
          syncStage: item.syncStage,
          fiscalStatus: item.fiscalStatus,
          printStatus: item.printStatus,
          cashNames: item.cashNames,
          total: item.total,
          products: item.products,
          attempts: item.attempts,
          lastAttemptAt: item.lastAttemptAt,
          lastErrorAt: item.lastErrorAt,
          error: item.error,
          request: {
            customers_id:       sd.customers_id       || null,
            staff_id:           sd.staff_id           || null,
            points_sales_id:    sd.points_sales_id    || null,
            types_vouchers_id:  sd.types_vouchers_id  || null,
            methods_payment_id: sd.methods_payment_id || null,
            price_list_id:      sd.price_list_id      || null,
            selectTypeVoucher:  sd.selectTypeVoucher  || null,
            date:               sd.date               || null,
            generateRemits:     sd.generateRemits     || null,
            from_sales_counter: sd.from_sales_counter || null,
          },
        }
        this.copyText(JSON.stringify(payload, null, 2))
      },
      copyText(text) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text)
            .then(() => this.showToast('Datos copiados', 'success'))
            .catch(() => this.copyTextFallback(text))
          return
        }
        this.copyTextFallback(text)
      },
      copyTextFallback(text) {
        const el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        try {
          document.execCommand('copy')
          this.showToast('Datos copiados', 'success')
        } catch(e) {
          this.showToast('No se pudieron copiar los datos', 'warning')
        }
        document.body.removeChild(el)
      },
      async recoverToForm(item) {
        const sd = item.saleData || {}
        const products = sd.detailProducts || []
        const concepts = sd.detailConcepts || []
        const itemsToRecover = products.length + concepts.length
        const total = this.formatCurrency(item.total)

        if (this.items.length > 0) {
          await this.$bvModal.msgBoxOk(
            this.$createElement('div', { domProps: { innerHTML:
              `Hay <strong>${this.items.length}</strong> ítem(s) en el ticket actual.<br><br>` +
              `Para recuperar esta factura primero cancelá o finalizá el ticket en curso.`
            }}),
            { title: 'No se puede recuperar ahora', okVariant: 'secondary', centered: true }
          )
          return
        }

        const productLines = products.map(p =>
          `<li>${p.product ? p.product.name : '—'} × ${p.quantity}</li>`
        ).concat(concepts.map(c =>
          `<li>${c.description || (c.concepts && c.concepts.name) || 'Concepto'} × ${c.quantity}</li>`
        )).join('')

        const customerLabel = sd.customer_name || `ID ${sd.customers_id}`
        const pointSaleLabel = sd.points_sales
          ? this.formatPointSaleLabel(sd.points_sales)
          : (sd.points_sales_id ? `ID ${sd.points_sales_id}` : '—')
        const isElectronic = sd.from_electronic_sale

        const ok = await this.$bvModal.msgBoxConfirm(
          this.$createElement('div', { domProps: { innerHTML:
            `Los ítems se van a cargar en el ticket y el contexto original se va a restaurar.<br><br>` +
            `<strong>Cliente:</strong> ${customerLabel}<br>` +
            `<strong>Punto de venta:</strong> ${pointSaleLabel}` +
            (isElectronic ? ` <span style="color:#0d6efd;font-weight:700">(Electrónico)</span>` : '') +
            `<br><strong>Total original:</strong> ${total}<br>` +
            `<ul class="mt-2 mb-2">${productLines}</ul>` +
            `<hr class="my-2">` +
            `<strong class="text-danger">⚠ Esta factura se elimina de la cola.</strong> Si no la volvés a cobrar, se pierde.`
          }}),
          {
            title: 'Recuperar factura para corregir',
            okTitle: 'Sí, cargar factura',
            cancelTitle: 'Cancelar',
            okVariant: 'primary',
            cancelVariant: 'outline-secondary',
            centered: true,
          }
        )
        if (!ok) return

        // Restaurar punto de venta original
        if (sd.points_sales_id) {
          this.ctx.points_sales_id = sd.points_sales_id
          this.ctx.points_sales = sd.points_sales || null
          this.contextSelection.pointSaleId = sd.points_sales_id
        }

        // Restaurar lista de precios original
        if (sd.price_list_id) {
          this.ctx.price_list_id = sd.price_list_id
          this.ctx.price_list = sd.price_list || null
        }

        // Restaurar método de pago original
        if (sd.methods_payment_id) {
          this.ctx.defaultMethodsPaymentId = sd.methods_payment_id
          this.ctx.defaultMethodsPayment = null
        }

        // Restaurar tipo de comprobante original — solo si el saleData trae un valor válido.
        // Si es null (venta encolada con contexto incompleto), mantener el del ctx actual
        // para no pisar un valor correcto ya resuelto.
        if (sd.types_vouchers_id) {
          this.ctx.types_vouchers_id = sd.types_vouchers_id
          this.ctx.types_vouchers = sd.types_vouchers || null
        }

        // Restaurar cliente y recargar datos dependientes
        if (sd.customers_id) {
          this.ctx.customers_id = sd.customers_id
          this.ctx.customer = { id: sd.customers_id, name: sd.customer_name || '' }
          serviceAPI.mostrarCliente(sd.customers_id)
            .then(response => { if (response.data) this.ctx.customer = response.data })
            .catch(() => {})
          this.getFinancesCash()
          if (this.ctx.customers_id && this.ctx.points_sales_id) {
            this.searchVoucherByCustomer()
          }
        }

        const restoredProducts = products.map(dp => ({
          _uid: `${Date.now()}_${Math.random()}`,
          item_type: 'product',
          product: { ...(dp.product || {}), source: 'product' },
          product_id: dp.product ? dp.product.id : 0,
          barcode: dp.product ? (dp.product.barcode || dp.product.code || '') : '',
          code: dp.product ? dp.product.code : '',
          name: dp.product ? dp.product.name : '',
          quantity: dp.quantity || 1,
          price: parseFloat(dp.price_final || dp.amount || 0),
          price_net: parseFloat(dp.amount || 0),
          aliquot_iva: dp.aliquot_iva || null,
          price_list: dp.price_list || null,
          currency: dp.currency || null,
          unit_measurement: dp.unit_measurement || null,
        }))
        const restoredConcepts = concepts.map(dc => ({
          _uid: `${Date.now()}_${Math.random()}`,
          item_type: 'concept',
          concept: { ...(dc.concepts || {}) },
          product: null,
          product_id: 0,
          barcode: '',
          code: '',
          name: dc.description || (dc.concepts && dc.concepts.name) || 'Concepto',
          quantity: dc.quantity || 1,
          price: parseFloat(dc.price_final || this.getConceptFinalPrice(dc.price, dc.concepts) || 0),
          price_net: parseFloat(dc.price || 0),
          aliquot_iva: this.getConceptIvaRate(dc.concepts) || 0,
          price_list: null,
          currency: null,
          unit_measurement: null,
        }))
        this.items = restoredProducts.concat(restoredConcepts)

        this.generalDiscount.percentage = this.clampGeneralDiscount(sd.percentage_discount)
        this.generalDiscount.percentageInput = this.formatDiscountPercentage(this.generalDiscount.percentage)

        // Guardar selección de medios de pago para pre-aplicar en openPayment()
        this._pendingPaymentRestore = (sd.payment && sd.payment.cash && sd.payment.cash.detail) || null

        this.discardQueueItem(item.id)
        this.modal.queueErrors.active = false
        this.paymentShow = false
        this.showToast(`${itemsToRecover} ítem(s) recuperados con el contexto original`, 'success')
        this.$nextTick(() => this.focusBarcode())
      },
      async confirmDiscardQueueItem(item) {
        const total = this.formatCurrency(item.total)
        const cashInfo = item.cashNames ? `<br><strong>Caja:</strong> ${item.cashNames}` : ''
        const errorInfo = item.error
          ? `<br><br><strong>Error registrado:</strong><br><code style="font-size:0.8rem;word-break:break-all">${item.error}</code>`
          : ''
        const ok = await this.$bvModal.msgBoxConfirm(
          this.$createElement('div', { domProps: { innerHTML:
            `<div class="text-danger font-weight-bold mb-2">⚠ Esta acción es irreversible</div>` +
            `Esta venta <strong>no se enviará al servidor</strong> y se perderá definitivamente.<br>` +
            `<strong>Total:</strong> ${total}${cashInfo}${errorInfo}`
          }}),
          {
            title: '¿Descartás esta factura con error?',
            okTitle: 'Sí, descartar definitivamente',
            cancelTitle: 'Cancelar',
            okVariant: 'danger',
            cancelVariant: 'outline-secondary',
            centered: true,
            headerBgVariant: 'danger',
            headerTextVariant: 'white',
          }
        )
        if (!ok) return
        this.discardQueueItem(item.id)
      },
      discardQueueItem(id) {
        saleQueue.discardItem(id)
        this.modal.queueErrors.items = saleQueue.getOpenItems()
        this.modal.queueErrors.recentDone = saleQueue.getRecentDoneItems()
        if (!saleQueue.hasBlockingItems()) this.modal.queueErrors.active = false
      },
      retryQueueItem(id) {
        if (!this.apiOnline) {
          this.showToast('Sin conexión. El comprobante se enviará cuando vuelva la conexión.', 'warning', 3500)
          return
        }
        saleQueue.retryItem(id)
        this.modal.queueErrors.items = saleQueue.getOpenItems()
        this.modal.queueErrors.recentDone = saleQueue.getRecentDoneItems()
        if (!saleQueue.hasBlockingItems()) this.modal.queueErrors.active = false
      },
      queueStatusLabel(status) {
        if (status === 'processing') return 'Guardando'
        if (status === 'failed') return 'Error'
        return 'Pendiente'
      },
      queueStatusVariant(status) {
        if (status === 'processing') return 'info'
        if (status === 'failed') return 'danger'
        return 'warning'
      },
      queueSyncStageLabel(stage) {
        if (stage === 'syncing') return 'Sincronizando'
        if (stage === 'server_saved') return 'Guardada en servidor'
        if (stage === 'sync_failed') return 'Error de sincronización'
        return 'Registrada localmente'
      },
      queueSyncStageVariant(stage) {
        if (stage === 'server_saved') return 'success'
        if (stage === 'syncing') return 'info'
        if (stage === 'sync_failed') return 'danger'
        return 'secondary'
      },
      queueFiscalLabel(status) {
        if (status === 'authorized') return 'Autorizada'
        if (status === 'error') return 'Error fiscal'
        if (status === 'not_required') return 'No fiscal'
        if (status === 'pending') return 'Pendiente'
        return 'Sin dato'
      },
      queueFiscalVariant(status) {
        if (status === 'authorized') return 'success'
        if (status === 'error') return 'danger'
        if (status === 'not_required') return 'secondary'
        return 'warning'
      },
      queuePrintLabel(status) {
        if (status === 'local') return 'Impresa local'
        if (status === 'requested') return 'Solicitada'
        if (status === 'failed') return 'Error'
        if (status === 'not_required') return 'No requerida'
        return 'Pendiente'
      },
      queuePrintVariant(status) {
        if (status === 'local') return 'success'
        if (status === 'requested') return 'success'
        if (status === 'failed') return 'danger'
        if (status === 'not_required') return 'secondary'
        return 'warning'
      },
      queueHasBlockingItemsForCaja(caja) {
        return caja && saleQueue.hasBlockingItemsForCash(caja.id)
      },
      // ── Finanzas / Cajas ──────────────────────────────────────────────────────
      getFinancesCash() {
        serviceAPI.obtenerCajas().then(response => {
          const data = response.data
          this.setCajasStatus(data)
          this.arr.financesCash = []
          const freeMode = this.parameters.freeAddTypeOperation
          const ps = this.ctx.points_sales || {}
          const assignedCashId = this.assignedCashId
          const formNd = ps.nd
          const isValidND = c => freeMode || (c.nd === formNd)

          if (assignedCashId) {
            const c = data.find(c => c.id === assignedCashId && c.active && isValidND(c))
            if (c) this.arr.financesCash.push(c)
          }

          data.forEach(c => {
            if (c.shared && c.active && isValidND(c) && !this.arr.financesCash.find(e => e.id === c.id)) {
              this.arr.financesCash.push(c)
            }
          })
          // Cajas ya disponibles: refrescar el snapshot offline con ellas incluidas.
          this._persistCounterContext()
        })
      },
      requestCajaRecheck() {
        if (this.cajaRecheckBlocked) {
          this.cajaFlow.recheckPending = true
          return
        }
        this.cajaFlow.recheckPending = false
        this.checkCajaFlow()
      },
      onCajaOpened() {
        // Ventas encoladas con la caja cerrada fallaron al sincronizar: con la caja abierta ya entran.
        saleQueue.retryFailed()
        saleQueue.startProcessing()
      },
      // La caja asignada se abre/cierra en CashSession; la vista sólo la dispara.
      checkCajaFlow() {
        // Las promesas de carga pueden resolver con la vista ya destruida (sin ref).
        if (this.$refs.cashSession) this.$refs.cashSession.checkCajaFlow()
      },
      openCajaCierre() {
        if (this.$refs.cashSession) this.$refs.cashSession.openCajaCierre()
      },
      setCajasStatus(cajas = []) {
        this.arr.financesCashAll = cajas || []
        this.cajaFlow.loaded = true
        this.cajaFlow.totalCajas = (cajas || []).length
      },
      queueOpenItemsForCashIds(cashIds) {
        return saleQueue.getOpenItemsForCashIds(cashIds)
      },

      // ── Toast interno ────────────────────────────────────────────────────────
      showToast(message, type = 'success', duration = 2000) {
        const id = ++this._toastSeq
        this.scToasts.push({ id, message, type })
        setTimeout(() => {
          const idx = this.scToasts.findIndex(t => t.id === id)
          if (idx !== -1) this.scToasts.splice(idx, 1)
        }, duration)
      },

      // ── Layout sizing ────────────────────────────────────────────────────────
      _updateLayout() {
        this.$nextTick(() => {
          const el = this.$refs.scanScreen
          if (!el) return
          const top = el.getBoundingClientRect().top
          this.scanScreenMinHeight = Math.max(200, window.innerHeight - top - 10)
        })
      },

      // ── Audio feedback ───────────────────────────────────────────────────────
      _initAudioCtx() {
        try {
          if (!this._audioCtx || this._audioCtx.state === 'closed') {
            this._audioCtx = new (window.AudioContext || window.webkitAudioContext)()
          }
          if (this._audioCtx.state === 'suspended') this._audioCtx.resume()
        } catch (e) { this._audioCtx = null }
      },
      _playTones(tones) {
        const ctx = this._audioCtx
        if (!ctx || ctx.state === 'closed') return
        tones.forEach(([freq, type, start, dur, vol]) => {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.connect(gain)
          gain.connect(ctx.destination)
          osc.type = type
          osc.frequency.value = freq
          gain.gain.setValueAtTime(vol, ctx.currentTime + start)
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur)
          osc.start(ctx.currentTime + start)
          osc.stop(ctx.currentTime + start + dur)
        })
      },
      playBeepSuccess() {
        this._playTones([
          [1800, 'sine', 0,    0.055, 0.4],
          [1800, 'sine', 0.09, 0.055, 0.4],
        ])
      },
      playBeepError() {
        this._playTones([
          [280, 'square', 0,    0.15, 0.18],
          [180, 'square', 0.18, 0.22, 0.18],
        ])
      },

      // ── Modo inmersivo interno ─────────────────────────────────────────────────
      toggleFocusMode() {
        if (this.focusMode) {
          this.disableFocusMode()
        } else {
          this.enableFocusMode()
        }
        setTimeout(() => this._updateLayout(), 50)
      },
      enableFocusMode() {
        this.focusMode = true
        if (document.body) document.body.classList.add('sc-focus-mode')
      },
      disableFocusMode() {
        this.focusMode = false
        if (document.body) document.body.classList.remove('sc-focus-mode')
      },
      requestBrowserFullscreen() {
        if (typeof document === 'undefined' || document.fullscreenElement) return
        const target = document.documentElement
        if (target && typeof target.requestFullscreen === 'function') {
          target.requestFullscreen().catch(() => {})
        }
      },
      exitBrowserFullscreen() {
        if (typeof document === 'undefined' || !document.fullscreenElement) return
        if (typeof document.exitFullscreen === 'function') {
          document.exitFullscreen().catch(() => {})
        }
      },
      _onFullscreenChange() {
        if (!document.fullscreenElement && this.focusMode) {
          this.disableFocusMode()
        }
      },
    },
  }
</script>

<style scoped>
  .sales-counter-page {
    min-height: 100vh;
    background: var(--app-color-canvas);
  }
  .sc-mobile-header {
    position: sticky;
    top: 0;
    z-index: 100;
    margin-bottom: 8px;
    border: 0;
    border-radius: 0;
    background: var(--app-color-primary-900);
    color: var(--app-color-on-primary);
  }
  .sc-mobile-header >>> .card-body {
    padding: 12px 16px;
  }
  .sc-mobile-header .card-title,
  .sc-mobile-header .text-muted,
  .sc-mobile-header .sc-customer-config,
  .sc-mobile-header .sc-customer-config-label,
  .sc-mobile-header .sc-customer-config-value,
  .sc-mobile-header .sc-header-catalog {
    color: var(--app-color-on-primary) !important;
  }
  .sc-mobile-header .sc-customer-config,
  .sc-mobile-header .sc-header-catalog {
    opacity: 0.78;
  }
  .sc-mobile-header .sc-point-sale-select {
    background: var(--app-color-surface);
    color: var(--app-color-text);
  }
  .sc-mobile-header .sc-header-sep {
    background: rgba(255, 255, 255, 0.28);
  }
  .sc-header-sync-badge {
    background: rgba(255, 255, 255, 0.16) !important;
    color: var(--app-color-on-primary) !important;
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.15;
  }
  .sc-header-sync-line {
    display: flex;
    align-items: center;
  }
  .sc-header-sync-when {
    font-size: 0.85em;
    font-weight: 400;
    opacity: 0.9;
  }
  .sc-focus-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 1px solid rgba(255, 255, 255, 0.36);
    border-radius: 6px;
  }
  .sc-focus-toggle:hover,
  .sc-focus-toggle:focus {
    background: rgba(255, 255, 255, 0.14);
    text-decoration: none;
  }
  /* ── Cajas ───────────────────────────────────────────────── */
  /* ── Queue badges ────────────────────────────────────────── */
  .sales-counter-queue-retry { cursor: pointer; }
  .sales-counter-queue-retry:hover { opacity: 0.85; }
  .sales-counter-queue-error-pre {
    white-space: pre-wrap; word-break: break-all;
    font-size: 12px; max-height: 200px; overflow-y: auto;
    background: #f8f9fa; padding: 8px; border-radius: 4px;
  }
  .sc-customer-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .sc-change-customer-btn {
    padding: 2px 7px;
    line-height: 1.2;
  }
  .sc-customer-config {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 3px;
    color: #6c757d;
    font-size: 0.82rem;
    font-weight: 600;
  }
  .sc-customer-config span {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }
  .sc-customer-config-label {
    color: #343a40;
    font-weight: 800;
    margin-right: 3px;
  }
  .sc-customer-config-value {
    color: #6c757d;
    font-weight: 500;
  }
  .sc-point-sale-line {
    gap: 4px;
  }
  .sc-point-sale-select {
    display: inline-block;
    width: 330px;
    height: 24px;
    padding-top: 1px;
    padding-bottom: 1px;
    font-size: 0.78rem;
  }
  .sc-electronic-label {
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border-radius: 4px;
    background: #0d6efd;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 900;
    text-transform: uppercase;
  }
  @media (max-width: 768px) {
    .sc-point-sale-select {
      width: 240px;
    }
  }

  .sc-customer-modal-hint {
    font-size: 0.88rem;
    color: #6c757d;
    margin-bottom: 14px;
  }
  .sc-change-customer-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    background: #1a56c4;
    color: #fff;
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 14px;
    font-size: 0.95rem;
  }
  .sc-change-customer-banner-icon {
    font-size: 1.3rem;
    flex-shrink: 0;
  }
  .sc-change-customer-banner-warn {
    margin-left: auto;
    background: rgba(255,255,255,0.18);
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 0.82rem;
    white-space: nowrap;
  }

  /* ── Barcode input ───────────────────────────────────────── */
  .sc-barcode-icon {
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    font-size: 1.2rem;
  }
  .sc-barcode-card {
    flex: 0 0 auto;
    border-color: var(--app-color-border);
    background: var(--app-color-surface);
  }
  .sc-barcode-input {
    font-size: 1.25rem !important;
    height: 48px !important;
    letter-spacing: 1px;
    border-color: var(--app-color-border-strong);
    background: var(--app-color-surface);
  }
  .sc-barcode-input:focus {
    border-color: var(--app-color-primary);
    box-shadow: 0 0 0 2px var(--app-color-primary-soft);
  }
  .sc-search-shortcuts {
    color: var(--app-color-text-secondary);
  }
  .sc-quick-concept-icon {
    background: #e7f5ff;
    color: #087f5b;
  }
  .sc-quick-concept-input {
    border-color: #20a67a !important;
    background: #f1fffa !important;
    box-shadow: 0 0 0 2px rgba(32, 166, 122, 0.14) !important;
  }
  .sc-quick-concept-hint {
    display: inline-flex;
    align-items: center;
    margin-top: 6px;
    padding: 4px 8px;
    border-radius: 5px;
    background: #e7f5ff;
    color: #087f5b;
    font-size: 0.78rem;
    font-weight: 700;
  }
  .sc-header-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 48px;
    padding: 0 14px;
    border-radius: 8px;
    cursor: pointer;
    user-select: none;
    font-weight: 700;
    font-size: 0.9rem;
    transition: filter 0.15s;
    white-space: nowrap;
  }
  .sc-header-btn:hover { filter: brightness(0.9); }
  .sc-header-btn-primary   { background: var(--app-color-primary-soft); color: var(--app-color-primary); border: 2px solid var(--app-color-primary); }
  .sc-header-btn-primary-on { background: var(--app-color-primary); color: var(--app-color-on-primary); border: 2px solid var(--app-color-primary); }
  .sc-header-btn-purple    { background: var(--app-color-surface-subtle); color: var(--app-color-text); border: 2px solid var(--app-color-border-strong); }
  .sc-header-btn-icon { font-size: 1.1rem; flex-shrink: 0; }
  .sc-header-btn-label { display: flex; align-items: center; gap: 5px; }
  .sc-header-kbd {
    font-size: 10px;
    padding: 1px 5px;
    border-radius: 4px;
    background: rgba(0,0,0,0.12);
    color: inherit;
    font-family: monospace;
    border: none;
    box-shadow: none;
    line-height: 1.4;
  }

  /* ── Keyboard shortcuts bar ──────────────────────────────── */
  .sc-shortcuts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .sc-shortcut {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #555;
  }
  .sc-shortcut kbd {
    background: #f0f0f0;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 1px 6px;
    font-size: 11px;
    font-family: monospace;
    color: #333;
    box-shadow: 0 1px 0 #aaa;
  }

  /* ── Items card ──────────────────────────────────────────── */
  /* flex-basis 0: la lista ocupa solo el espacio libre y scrollea por dentro, sin empujar la barra inferior */
  .sc-items-card { padding: 0 !important; overflow: hidden; flex: 1 1 0; min-height: 0; display: flex; flex-direction: column; }
  .sc-items-card .card-body { padding: 0 !important; flex: 1 1 auto; overflow-y: auto; min-height: 0; }
  .sc-items-empty { border: 2px dashed #dee2e6 !important; }
  .sc-items-empty .card-body { padding: 1rem !important; display: flex; align-items: center; justify-content: center; }

  /* Table header */
  .sc-table >>> thead th {
    background: #1a1a2e !important;
    color: #c9d1e0 !important;
    font-size: 13px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.6px !important;
    padding: 10px 12px !important;
    border: none !important;
  }
  /* Table rows */
  .sc-table >>> tbody td {
    padding: 10px 12px !important;
    vertical-align: middle !important;
    border-color: #f0f2f5 !important;
  }
  .sc-table >>> tbody tr:nth-child(odd) td  { background: #edf1f7; }
  .sc-table >>> tbody tr:nth-child(even) td { background: #d8e1ef; }
  .sc-table >>> tbody tr:hover td { background: #c8d4e8 !important; }

  /* Row states */
.sc-row-selected >>> td { background: #e3f2fd !important; }

  /* Product cell */
  .sc-row-num   { color: #4a5568; font-size: 1.1rem; font-weight: 700; }
  .sc-prod-name { font-size: 0.92rem; font-weight: 600; color: #1a202c; line-height: 1.3; }
  .sc-prod-code { font-size: 11px; color: #4a5568; font-family: monospace; margin-top: 2px; }

  /* Price cells */
  .sc-price   { color: #495057; font-size: 0.9rem; display: block; text-align: right; }
  .sc-subtotal { font-size: 1rem; color: #1a1a2e; display: block; text-align: right; }

  /* Quantity input */
  .sc-qty-input { border-radius: 6px !important; font-weight: 600 !important; font-size: 1.05rem !important; min-height: 38px !important; }

  /* Delete button */
  .sc-del-btn { border-radius: 6px !important; padding: 3px 8px !important; }

  /* ── Empty state ─────────────────────────────────────────── */
  .sc-empty-state { color: #6c757d; }
  .sc-empty-icon { font-size: 3.5rem; display: block; margin: 0 auto; }
  .sc-empty-text { font-size: 1.1rem; font-weight: 500; }

  /* ── Scan screen layout ──────────────────────────────────── */
  .sc-scan-screen,
  .sc-payment-screen,
  .sc-customer-selector-screen {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .sc-payment-screen .sc-payment-card {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  /* ── Bottom bar ──────────────────────────────────────────── */
  .sc-bottom-bar {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
    position: sticky;
    bottom: 0;
    z-index: 20;
    margin-top: auto;
    /* Nunca se achica: totales y botones quedan siempre visibles */
    flex: 0 0 auto;
  }
  .sc-total-area {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    background: #1a1a2e;
    color: #fff;
  }
  .sc-total-label { font-size: 1rem; font-weight: 600; opacity: 0.9; }
  .sc-total-left { display: flex; flex-direction: column; gap: 5px; }
  .sc-total-summary { margin-left: auto; text-align: right; }
  .sc-total-discount { color: #bce8c7; font-size: 0.82rem; font-weight: 600; margin-bottom: 1px; }
  .sc-total-amount { font-size: 2rem; font-weight: 800; letter-spacing: -1px; }
  .sc-total-discount-control { display: flex; align-items: center; gap: 7px; }
  .sc-total-discount-control label {
    margin: 0;
    color: rgba(255,255,255,.85);
    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .sc-total-discount-input-group { display: flex; align-items: center; gap: 5px; }
  .sc-total-discount-input-group .form-control { width: 68px; height: 31px; padding: 3px 7px; text-align: right; font-weight: 700; }
  .sc-total-discount-input-group span { color: rgba(255,255,255,.9); font-weight: 700; }
  @media (max-width: 575.98px) {
    .sc-total-area { gap: 8px; }
    .sc-total-amount { font-size: 1.65rem; }
  }
  .sc-header-catalog { font-size: 0.82rem; font-weight: 500; white-space: nowrap; }
  .sc-header-sep { width: 1px; height: 22px; background: #dee2e6; flex-shrink: 0; align-self: center; }
  .sc-connection-badge {
    align-items: center;
    display: inline-flex;
    font-size: 0.82rem;
    font-weight: 800;
    line-height: 1.2;
    white-space: nowrap;
  }
  .sc-connection-badge-online {
    background: #1f8f46 !important;
  }
  .sc-connection-badge-offline {
    background: #c82333 !important;
  }

  /* ── Errores de configuración ────────────────────────────── */
  .sc-config-errors {
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #dc3545;
    margin-top: 4px;
  }
  .sc-config-errors-header {
    background: #dc3545;
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    padding: 14px 20px;
    display: flex;
    align-items: center;
  }
  .sc-config-error-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 20px;
    border-bottom: 1px solid #f1f1f1;
    background: #fff;
  }
  .sc-config-error-item:last-child { border-bottom: none; }
  .sc-config-error-danger  { border-left: 5px solid #dc3545; }
  .sc-config-error-warning { border-left: 5px solid #ffc107; }
  .sc-config-error-icon {
    font-size: 1.8rem;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .sc-config-error-danger  .sc-config-error-icon { color: #dc3545; }
  .sc-config-error-warning .sc-config-error-icon { color: #e0a800; }
  .sc-config-error-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 4px;
  }
  .sc-config-error-detail {
    font-size: 0.9rem;
    color: #6c757d;
    line-height: 1.5;
  }
  .sc-action-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    gap: 8px;
    background: #f8f9fa;
  }
  .sc-btn-cancel { }
  .sc-btn-action { position: relative; }
  .sc-btn-cobrar {
    font-size: 1.1rem;
    padding: 8px 20px;
    font-weight: 600;
    background-color: #1a6e2e !important;
    border-color: #155a26 !important;
  }
  .sc-btn-cobrar:hover:not(:disabled) {
    background-color: #155a26 !important;
    border-color: #0f4219 !important;
  }

  /* ── Payment screen ─────────────────────────────────────── */
  .sc-payment-screen .sc-payment-card > .card-header {
    background: #1a1a2e;
    color: #fff;
    border-bottom: none;
  }
  .sc-payment-screen .sc-payment-card > .card-header h5 {
    color: #fff;
  }
  .sc-payment-total-header {
    font-size: 1.8rem;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: #fff;
  }
  .sc-pay-num {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: #dee2e6;
    color: #555;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    flex-shrink: 0;
    font-family: monospace;
    border: 1px solid #ccc;
  }
  .sc-pay-num-on {
    background: #4285f4;
    color: #fff;
    border-color: #2563eb;
  }
  /* ── Payment methods list ───────────────────────────────── */
  .sc-section-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6c757d;
  }
  .sc-methods-list {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
  }
  .sc-method-row {
    display: flex;
    align-items: center;
    padding: 9px 14px;
    cursor: pointer;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.1s;
    gap: 10px;
  }
  .sc-method-row:last-child { border-bottom: none; }
  .sc-method-row:hover { background: #f8f9fa; }
  .sc-method-active { background: #e8f5e9 !important; }
  .sc-confirm-context {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    font-size: 0.78rem;
    color: #888;
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 10px;
  }
  .sc-confirm-context span {
    display: flex;
    align-items: center;
  }
  .sc-confirm-section-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #fff;
    background: #3d6ea8;
    padding: 3px 10px;
    border-radius: 4px;
    display: inline-block;
    margin-bottom: 8px;
  }
  .sc-confirm-columns {
    display: grid;
    grid-template-columns: 7fr 3fr;
    gap: 16px;
    align-items: start;
  }
  .sc-confirm-col { min-width: 0; }
  .sc-confirm-products {
    border: 1px solid #c9d8ee;
    border-radius: 8px;
    overflow: hidden;
  }
  .sc-confirm-products-scroll {
    max-height: 230px;
    overflow-y: auto;
  }
  .sc-confirm-product-row {
    display: flex;
    align-items: center;
    padding: 9px 14px;
    border-bottom: 1px solid #e8eef7;
    gap: 10px;
    background: #fff;
  }
  .sc-confirm-product-row:nth-child(even) { background: #f0f5ff; }
  .sc-confirm-product-row:last-child { border-bottom: none; }
  .sc-confirm-product-name { flex: 1; font-weight: 500; }
  .sc-confirm-product-qty  { color: #6c757d; font-size: 0.9rem; white-space: nowrap; }
  .sc-confirm-product-price { font-weight: 700; white-space: nowrap; min-width: 110px; text-align: right; color: #1a56a0; }
  .sc-confirm-col-right {
    background: #1a56a0;
    border-radius: 10px;
    padding: 16px;
  }
  .sc-confirm-col-right .sc-confirm-section-label {
    background: rgba(255,255,255,0.2);
    color: #fff;
  }
  .sc-confirm-col-right .sc-confirm-products {
    border-color: rgba(255,255,255,0.2);
  }
  .sc-confirm-col-right .sc-confirm-product-row {
    background: rgba(255,255,255,0.08);
    border-bottom-color: rgba(255,255,255,0.12);
    color: #fff;
  }
  .sc-confirm-col-right .sc-confirm-product-row:nth-child(even) { background: rgba(255,255,255,0.15); }
  .sc-confirm-col-right .sc-confirm-product-name { color: #d6e8ff; }
  .sc-confirm-col-right .sc-confirm-product-price { color: #fff; }
  .sc-confirm-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    background: rgba(255,255,255,0.15);
    border-radius: 8px;
    font-weight: 600;
    color: #fff;
    margin-top: 10px;
    border: 1px solid rgba(255,255,255,0.25);
  }
  .sc-confirm-discount-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    color: #d6e8ff;
    font-size: 0.86rem;
    margin: -4px 0 10px;
  }
  .sc-confirm-discount-row strong { color: #fff; white-space: nowrap; }
  .sc-confirm-total-amount { font-size: 1.5rem; font-weight: 700; color: #fff; }

  .sc-payment-diff-alert {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    border-radius: 10px;
    border-width: 2px;
    border-style: solid;
  }
  .sc-payment-diff-danger  { background: #f8d7da; border-color: #f5c6cb; color: #721c24; }
  .sc-payment-diff-warning { background: #fff3cd; border-color: #ffc107; color: #856404; }
  .sc-payment-diff-icon { font-size: 2rem; flex-shrink: 0; }
  .sc-payment-diff-title { font-size: 0.85rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
  .sc-payment-diff-amount { font-size: 1.8rem; font-weight: 700; line-height: 1.1; }

  .sc-amount-block {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .sc-amount-label {
    font-size: 0.8rem;
    color: #6c757d;
    white-space: nowrap;
  }
  .sc-amount-input {
    width: 160px;
    font-size: 1.1rem;
    font-weight: 600;
    text-align: right;
  }

  /* ── Lista de comprobantes (modal de cola) ─────────────── */
  .sc-cierre-cpbt-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 13px;
  }
  .sc-cierre-cpbt-header {
    display: grid;
    grid-template-columns: 1fr 110px 150px;
    gap: 16px;
    padding: 6px 12px;
    background: #343a40;
    color: #adb5bd;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    position: sticky;
    top: 0;
  }
  .sc-cierre-cpbt-row {
    display: grid;
    grid-template-columns: 1fr 110px 150px;
    gap: 16px;
    padding: 7px 12px;
    border-bottom: 1px solid #f0f0f0;
    align-items: center;
  }
  .sc-cierre-cpbt-row:last-child { border-bottom: none; }
  .sc-cierre-cpbt-row:hover { background: #f8f9fa; }
  .sc-cierre-cpbt-total { font-weight: 700; text-align: right; white-space: nowrap; }

  /* ── Keyboard hint badge inside buttons ──────────────────── */
  .sc-kbd-hint {
    display: inline-block;
    background: rgba(0,0,0,0.15);
    border-radius: 4px;
    padding: 0 5px;
    font-size: 11px;
    font-family: monospace;
    margin-left: 6px;
    line-height: 1.6;
    vertical-align: middle;
  }
  .sc-kbd-hint-light {
    background: rgba(255,255,255,0.25);
    color: #fff;
  }
  .sc-kbd-hint-secondary {
    background: rgba(0,0,0,0.08);
    color: #555;
  }

  /* ── Product search ──────────────────────────────────────── */
  .sc-search-results {
    position: absolute;
    z-index: 1050;
    left: 0;
    right: 0;
    top: calc(100% + 2px);
    background: #fff;
    border: 1px solid #ced4da;
    border-radius: 0.375rem;
    box-shadow: 0 6px 20px rgba(0,0,0,0.15);
    max-height: 320px;
    overflow-y: auto;
  }
  .sc-search-empty {
    padding: 12px 14px;
    font-size: 0.875rem;
  }
  .sc-search-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.1s;
  }
  .sc-search-item:last-child { border-bottom: none; }
  .sc-search-item:hover,
  .sc-search-item-active {
    background: #e8f4ff;
  }
  .sc-search-item-body { flex: 1; min-width: 0; }
  .sc-search-item-name {
    font-weight: 600;
    font-size: 0.9rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sc-search-item-meta {
    font-size: 0.775rem;
    color: #6c757d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sc-search-code { font-family: monospace; }
  .sc-search-item-price {
    font-weight: 700;
    font-size: 0.9rem;
    white-space: nowrap;
    color: #1a6e2e;
  }
</style>

<style>
/* ── Modal cambiar cliente ───────────────────────────────────── */
.sc-customer-modal .modal-content {
  border: none;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}
.sc-customer-modal .modal-header {
  background: #1a1a2e;
  padding: 14px 20px;
  border-bottom: none;
}
.sc-customer-modal .modal-body {
  padding: 20px;
}
.sc-customer-modal-header {
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 1.05rem;
  font-weight: 600;
  width: 100%;
}
.sc-customer-modal-header-warn {
  font-size: 0.78rem;
  font-weight: 400;
  opacity: 0.75;
}
.sc-customer-modal-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 14px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
}

body.sc-focus-mode .sc-action-grid {
  left: 0 !important;
}

body.sc-focus-mode .app-header,
body.sc-focus-mode .sidebar,
body.sc-focus-mode .aside-menu,
body.sc-focus-mode .app-footer,
body.sc-focus-mode .alert-clear-cache,
body.sc-focus-mode .shortcuts-dropdown-full {
  display: none !important;
}
body.sc-focus-mode .app-body {
  margin-top: 0 !important;
}
body.sc-focus-mode .main {
  margin-left: 0 !important;
  width: 100% !important;
}

body.sc-focus-mode .main .container-fluid {
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
}

/* ── Toast interno Venta Mostrador ── */
.sc-toast-wrap {
  position: fixed;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.sc-toast-item {
  width: 540px;
  max-width: 90vw;
  padding: 9px 22px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 400;
  color: #fff;
  box-shadow: 0 6px 20px rgba(0,0,0,0.2);
  pointer-events: auto;
}
.sc-toast-success { background: #1e7e34; }
.sc-toast-warning { background: #d35400; }
.sc-toast-error   { background: #c0392b; }
.sc-toast-info    { background: #2471a3; }

.sc-toast-enter-active { animation: sc-toast-in  150ms ease; }
.sc-toast-leave-active { animation: sc-toast-out 150ms ease; }
@keyframes sc-toast-in  { from { opacity: 0; } to { opacity: 1; } }
@keyframes sc-toast-out { from { opacity: 1; } to { opacity: 0; } }
</style>
